import React, { Component } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Radio,
  Spin,
  Tag,
  Empty,
  Table,
  Typography,
  Space,
  Progress,
  Popover,
  Tooltip,
  Alert,
  Button,
  InputNumber,
  Skeleton,
  Select,
} from "antd";
import {
  RiseOutlined,
  FallOutlined,
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { PmsAPI } from "../../controller/PmsAPI";
import ResponsiveECharts from "../components/ResponsiveECharts";
import DateWithWeekday from "../components/DateWithWeekday";
import { METRIC_COLOR, WEEKEND_COLOR, SEGMENT_COLOR, PACE_COLOR, PACE_MARK, MUTED_COLOR, FAINT_COLOR, MARK_MUTED_COLOR } from "../components/metricColors";
import { kAxis } from "../components/axisFormat";
import FeedbackSummaryCard from "../components/FeedbackSummaryCard";
import SatisfactionSummary from "../components/FeedbackSummaryCard/SatisfactionSummary";
import { FONT } from "../styles/typography";
import PricingActionsCard from "./pricingActions/PricingActionsCard";
import OrdersDrawer from "./orderLookup/OrdersDrawer";
import CurveDrawer from "./pickupCurve/CurveDrawer";
import ForecastTable from "./forecastTable/ForecastTable";
import { holidaySamples, holidaySampleText, sampleRange } from "../components/bandSamples";
import { withKeyboardSort } from "../components/keyboardSort";
import { fetchSpecialDates } from "../../controller/holidayCalendar";
import { pickupOrdersRange } from "./orderLookup/range";
import { LOWER_MAX_LEAD } from "./pricingActions/rules";

const CH_COLORS = ["#1890ff", "#13c2c2", "#faad14", "#722ed1", "#eb2f96", "#52c41a", MUTED_COLOR];
const WD = ["一", "二", "三", "四", "五", "六", "日"]; // isoWeekday 1..7
const wdc = (m) => WD[m.isoWeekday() - 1];
const fmtDay = (m) => `${m.format("M/D")} (${wdc(m)})`; // 每日：星期與日期以空白間隔

const isWeekendLabel = (v) => typeof v === "string" && (v.endsWith("(六)") || v.endsWith("(日)"));
// 圖表線色標準：與 KPI 卡徽章同色，同指標跨圖一致；本期=實線、前期(去年)=虛線。中位/正常帶維持中性灰。


// 各區塊決策提示 (由設計 tips() 移植)
const TIPS = {
  occupancy: ["高於目標：調升尖峰日房價、收回折扣、設定最短住宿晚數、房量優先給直訂", "低於目標：開放 OTA 房量、上架限時直訂優惠、推早鳥/連泊專案、放寬彈性取消"],
  adr: ["偏低：改動態定價、主打房型升等與加價服務、綁套裝拉高客單、減少低價 OTA 額度", "達標：維持定價、測試尖峰再加價、以加購擴大 total revenue"],
  revpar: ["RevPAR = ADR × 住房率，兩者需平衡", "住房高→拉價；住房低→衝量", "分房型檢視，別被整館平均掩蓋"],
  netrevpar: ["Net RevPAR = 每房淨營收 (扣掉平台以毛額記帳的佣金後、飯店實收 ÷ 可售房數)", "與 RevPAR 的差距＝被平台以毛額收取的佣金；差距擴大代表高佣通路佔比上升", "獲客成本 (估) 含所有平台抽成；降低靠通路轉移 (官網專屬房型/會員/最優價保證)"],
  trevpar: ["TRevPAR (含餐) = (房租 + 餐飲・服務) ÷ 可售房數，衡量每間可售房的總營收", "與 RevPAR 的差距＝餐飲/加購貢獻，差距大代表非房營收強", "偏低：加開餐飲/宴會/加購方案，提升每房總產值"],
  revenue: ["落後：優先補去化慢的房型與日期、加開餐飲/宴會/SPA 帶動非房收", "超越：守住 ADR 避免不必要折扣、投資回頭客與口碑"],
  forecast: ["OTB＝On The Books（在手訂房）：未來各期「已成立訂單」的在手住房率，用來預判尚未成行的訂房展望。", "淺藍正常帶＝近 5 年『同提前天數 (同星期)』的在手分布 (P25–P75，以訂單成立/取消日回推)；柱高於帶=超前(可拉價)、低於=落後(需促銷)、帶內=正常。歷史為近似還原 (PMS 無逐筆異動歷程，約 8 成訂單成立後曾異動)，宜視為區間參考而非精確值。", "未來 OTB 偏低：提前開房量與促銷、鎖定淡日做專案", "未來 OTB 偏高：調升尖峰房價、設最短住宿晚數、安全範圍內適度超賣"],
  channel: ["OTA 佔比偏高：官網會員價強化直訂、經營 CRM 降獲客成本", "直訂健康：維持通路組合、持續會員經營"],
  velocity: ["訂房增速上下兩張圖同時看：上『依入住日』＝前瞻窗口內每個未來桶、近 30 天成立/取消且涵蓋該日的淨訂房 (哪些未來日在加溫/降溫，與前瞻同 x 軸)；下『依訂房日』＝近期訂房日對未來期新增/取消的淨房晚 (最近接單快不快)。兩圖皆依檢視粒度聚合 (月=逐日 / 季=逐週 / 年=逐月)，回看天數隨粒度加長。", "綠柱＝淨新增、紅柱＝淨取消 (取消≥新增)。上圖看『單灌到哪些未來日』；下圖看『下單動能』。標題徽章『近 7 日淨』即依訂房日的整體動能。"],
  roomtype: ["去化快：調升該房型房價、留庫存給直訂與高價通路", "去化慢：促銷或降門檻、引導付費升等、OTA 額度優先給該房型"],
  staffing: ["人力缺口：尖峰加派或跨部門支援、依 OTB 預先排班", "人力過剩：縮減平日班表、安排教育訓練與設備維護"],
};

function InfoTip({ id, title }) {
  const lines = TIPS[id];
  if (!lines) return null;
  return (
    <Popover
      title={title}
      content={
        <div style={{ maxWidth: 300 }}>
          {lines.map((l, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginTop: i ? 6 : 0 }}>
              <span style={{ color: "#1890ff", fontWeight: 700 }}>›</span>
              <span style={{ color: "#555" }}>{l}</span>
            </div>
          ))}
        </div>
      }
    >
      <InfoCircleOutlined style={{ color: MARK_MUTED_COLOR, marginLeft: 6, cursor: "help" }} />
    </Popover>
  );
}

function Spark({ data, color = "#1890ff" }) {
  if (!data || data.length < 2) return null;
  const W = 96, H = 26, p = 2;
  const mn = Math.min(...data), mx = Math.max(...data), sp = mx - mn || 1;
  const x = (i) => p + (i / (data.length - 1)) * (W - 2 * p);
  const y = (v) => p + (1 - (v - mn) / sp) * (H - 2 * p);
  const d = data.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  return (
    <svg width={W} height={H} style={{ display: "block" }}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
      <circle cx={x(data.length - 1)} cy={y(data[data.length - 1])} r={2.2} fill={color} />
    </svg>
  );
}

export class OpsDashboard extends Component {
  state = {
    narrow: typeof window !== "undefined" && window.innerWidth < 576,
    loading: false,
    breakdownLoading: false,
    gran: "month", // month | quarter | year
    offset: 0, // 0=本期 / -1 上一期 / +1 下一期
    breakdownMetric: "rev", // 訂房來源/通路/方案 顯示度量：rev 營收 / nights 房晚
    forecastView: "chart", // 前瞻卡：chart 長條圖 / table 表格 (表格是 canvas 的鍵盤與螢幕閱讀器等價入口)
    dayItems: [],
    monthItems: [],
    breakdown: null,
    breakdownCmp: null, // 對比期 breakdown (房型維度去年同期對照)
    kpiCur: null, // 本期 KPI (ops/kpi，純房租口徑)
    kpiCmp: null, // 已實現同進度對比 KPI (去年同期到昨天 STLY)
    kpiCmpFull: null, // 全期對比 KPI (去年最終值，非同進度)
    trendCmp: [], // 期間走勢/明細對照的對比期逐桶 (ops/series)：整段回推 cmpYears 年，配對規則見 cmpMatcher
    trendCur: [], // 同上，本期那一份。本地日資料只到 +5 個月，往後翻期會補 0 而畫成假的 0%，故改由後端取
    forecastCmp: [], // 前瞻窗口「去年同期實際」逐桶 (ops/series)，供訂房展望疊 LY 實際基準
    bandHolidays: new Set(), // 正常帶的對照樣本裡有哪些是國定假日 (農曆節慶會在 364 天對齊下漂移)
    pace: [], // 前瞻逐日在手 (ops/pace)：合計 + 團體/散客分層。與 paceBand 同為 ORDER_DT as-of 還原，柱與帶才可比
    paceBand: [], // 前瞻訂房曲線正常帶 (ops/pace-band)：過去 N 年同 lead OTB 的 P25–P75 + 去年同 lead
    paceBandLoading: false, // 正常帶 (超前/落後) 非阻塞載入中：查詢重 (多年回推達 10 秒級)，前瞻卡顯示提示避免以為卡住
    velocityLoading: false, // 「依訂房日」(ops/pace-velocity) 載入中
    pickupLoading: false,   // 「依入住日」(ops/pace-pickup) 載入中；兩張圖資料來源不同，各自標示
    velocity: [], // 訂房增速用 (ops/pace-velocity)：近 30 訂房日的每日新增/取消/淨，供 velocitySummary hot/cold 判定 + 「依訂房日」圖
    pickup: [], // 訂房增速圖用 (ops/pace-pickup)：前瞻窗口內各未來住用日的近期淨 pickup，與前瞻同 x 軸對照
    roomPickup: [], // 房型維度用 (ops/pace-pickup-room)：本期各房型近 30 日淨 pickup (售罄動能)
    growth: 8, // 營收成長目標 (%)：目標 = 對比期 RevPAR ×(1+g)；由 localStorage 載入、可於工具列調整
    cmpYears: 1, // 比較基準年份 (幾年前)：1=去年…5=五年前；作用於所有粒度的「同期」對比。由 localStorage 載入
    curveOpen: false, curveStay: null, curveFrom: null, curveTo: null, curveData: null, curveLoading: false, // 點前瞻柱 → 單日訂房曲線抽屜
    curveRoom: null, // 曲線的房型檢視 (null = 全館)
    ordersOpen: false, ordersTitle: null, ordersData: null, ordersLoading: false, ordersError: null, // 訂單明細抽屜 (訂房增速柱 / 訂房曲線的在手清單共用)
    error: null, // API 失敗訊息 (顯示可重試的錯誤區塊)
    feedback: null,
    feedbackError: false,
    feedbackNeeds: null,
    feedbackNeedsError: false,
  };

  // 住客體驗卡的「查看客戶意見回饋」要開的是摘要卡裡的明細抽屜。抽屜的取數與狀態
  // 都屬於回饋那一塊，留在 FeedbackSummaryCard 裡，這裡只借 ref 把它打開。
  feedbackCardRef = React.createRef();

  // 把 axios error 轉成使用者可讀訊息 (network error 時 e.response 可能不存在)
  errMsg = (e) => {
    const s = e && e.response && e.response.status;
    return s ? `資料載入失敗 (HTTP ${s})，請稍後重試。` : "資料載入失敗，請確認網路或連線後重試。";
  };

  // 行動版旗標。圖表的 option 由 class method 產生、拿不到 Grid.useBreakpoint (那是 hook)，
  // 所以自己聽 resize —— 圖例折行、fixed 欄、標籤密度都要靠它決定。
  onResize = () => {
    const narrow = window.innerWidth < 576; // antd xs
    if (narrow !== this.state.narrow) this.setState({ narrow });
  };

  componentDidMount() {
    window.addEventListener("resize", this.onResize);
    try { const v = localStorage.getItem("ops.growthTarget"); if (v != null && v !== "") this.setState({ growth: Number(v) }); } catch (e) { /* localStorage 不可用時用預設 */ }
    try { const y = Number(localStorage.getItem("ops.cmpYears")); if (y >= 1 && y <= 5) this.setState({ cmpYears: y }); } catch (e) { /* 用預設 */ }
    // 粒度、度量、前瞻檢視方式也一起記住：習慣看房晚的人每次重整都要重點一次，
    // 成長目標與比較基準年份早就存了，這三個沒理由例外
    try {
      const g = localStorage.getItem("ops.gran");
      if (["month", "quarter", "year"].includes(g)) this.setState({ gran: g });
      const m = localStorage.getItem("ops.breakdownMetric");
      if (["rev", "nights"].includes(m)) this.setState({ breakdownMetric: m });
      const v = localStorage.getItem("ops.forecastView");
      if (["chart", "table"].includes(v)) this.setState({ forecastView: v });
    } catch (e) { /* 用預設 */ }
    this.load();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
  }

  /** 設定 state 並寫進 localStorage；儲存失敗 (無痕模式、封鎖 site data) 不影響畫面。 */
  setPersisted = (key, storageKey, value) => {
    this.setState({ [key]: value });
    try { localStorage.setItem(storageKey, String(value)); } catch (e) { /* 忽略儲存失敗 */ }
  };

  // 調整營收成長目標 (%)，夾在 [-50,100] 並持久化到 localStorage
  persistGrowth = () => { try { localStorage.setItem("ops.growthTarget", String(this.state.growth)); } catch (e) { /* 忽略儲存失敗 */ } };
  setGrowth = (v) => this.setState({ growth: Math.max(-50, Math.min(100, Math.round(Number(v) || 0))) }, this.persistGrowth);
  // 以函式式 setState 累加，避免連點時讀到過期 state
  bumpGrowth = (delta) => this.setState((s) => ({ growth: Math.max(-50, Math.min(100, s.growth + delta)) }), this.persistGrowth);

  // 比較基準年份 (1~5 年前)：改後持久化並重抓 (對比區間隨之改變)。作用於所有粒度。
  setCmpYears = (y) => this.setState({ cmpYears: Math.max(1, Math.min(5, Number(y) || 1)) }, () => {
    try { localStorage.setItem("ops.cmpYears", String(this.state.cmpYears)); } catch (e) { /* 忽略 */ }
    this.fetchBreakdown();
  });
  // 對比要回推多少 (含選定 cmpYears)：一律 cmpYears 年 (日曆對齊，與 KPI 卡/明細對照/目標達成同口徑)
  cmpBack = () => ({ n: this.state.cmpYears || 1, u: "year" });
  // 比較基準的相對標籤 (去年/前年/…)
  cmpYearWord = () => ({ 1: "去年", 2: "前年", 3: "3 年前", 4: "4 年前", 5: "5 年前" }[this.state.cmpYears] || `${this.state.cmpYears} 年前`);

  load = async () => {
    this.setState({ loading: true, error: null });
    try {
      const [dayRes, monthRes] = await Promise.all([
        PmsAPI.houserate.stat("day"),
        PmsAPI.houserate.stat("month"),
      ]);
      this.setState({
        dayItems: this.norm(dayRes.data.items, "YYYY/MM/DD"),
        monthItems: this.norm(monthRes.data.items, "YYYY/MM"),
      });
    } catch (e) {
      this.setState({ error: this.errMsg(e) });
    } finally {
      this.setState({ loading: false });
    }
    // 與住房統計解耦：即使上面失敗，仍嘗試載入 KPI/breakdown (獨立端點)
    this.fetchBreakdown();
  };

  // fallbackRooms 由端點回傳的 capacity 帶入 —— 房數是 PMS 的資料，前端不該有寫死的常數
  norm = (items, fmt, fallbackRooms = 0) =>
    (items || [])
      .map((it) => ({
        dat: it.dat,
        m: moment(it.dat, fmt),
        use_qnt: Number(it.use_qnt) || 0,
        rent_amt: Number(it.rent_amt) || 0,
        rs_amt: Number(it.rs_amt) || 0,
        adr: Number(it.adr) || 0,
        revpar: Number(it.revpar) || 0,
        occupancy_rate: Number(it.occupancy_rate) || 0,
        total_revenue: Number(it.total_revenue) || 0,
        total_rooms: Number(it.total_rooms) || fallbackRooms,
      }))
      .filter((it) => it.m.isValid())
      .sort((a, b) => a.m.valueOf() - b.m.valueOf());

  unitOf = (gran) => gran;  // moment 單位 (month/quarter/year 同名)
  stepOf = (gran) => gran;

  // 指定 offset 的期間範圍 (一律回完整期間，含在手；本期 offset=0 亦到期末)
  viewRange = (gran, offset) => {
    const anchor = moment().add(offset, this.stepOf(gran));
    const from = anchor.clone().startOf(this.unitOf(gran));
    const to = anchor.clone().endOf(this.unitOf(gran)); // 全期 (原 offset 0 截今天，改期末以含在手)
    return { from, to };
  };

  // 本期已實現部分：期首 → min(期末, 昨天)。未來期無已實現 → to=null。
  realizedRange = (gran) => {
    const { from, to } = this.viewRange(gran, this.state.offset);
    const yEnd = moment().subtract(1, "day").endOf("day");
    if (yEnd.isBefore(from)) return { from: from.clone(), to: null };
    return { from: from.clone(), to: moment.min(to, yEnd) };
  };
  fullRange = (gran) => this.viewRange(gran, this.state.offset);
  // 已實現同進度對比 (realized 回推選定基準年)；無已實現則 null
  realizedCompareRange = (gran) => {
    const r = this.realizedRange(gran);
    if (!r.to) return null;
    const b = this.cmpBack(gran);
    return { from: r.from.clone().subtract(b.n, b.u), to: r.to.clone().subtract(b.n, b.u) };
  };
  // 全期 vs 選定基準年最終對比 (full 回推選定基準年)
  fullCompareRange = (gran) => {
    const { from, to } = this.fullRange(gran);
    const b = this.cmpBack(gran);
    return { from: from.clone().subtract(b.n, b.u), to: to.clone().subtract(b.n, b.u) };
  };

  // 把某月的日列彙總成一個「部分月」合成列 (當月用，避免整月未來預估)
  synthMonth = (mStart, days) => {
    const s = days.reduce((a, it) => { a.use += it.use_qnt; a.cap += it.total_rooms; a.rent += it.rent_amt; a.rs += it.rs_amt; a.rev += it.total_revenue; return a; }, { use: 0, cap: 0, rent: 0, rs: 0, rev: 0 });
    return { dat: mStart.format("YYYY/MM"), m: mStart.clone(), use_qnt: s.use, total_rooms: s.cap, rent_amt: s.rent, rs_amt: s.rs, total_revenue: s.rev, occupancy_rate: s.cap ? (s.use / s.cap) * 100 : 0, adr: s.use ? s.rent / s.use : 0, revpar: s.cap ? s.rent / s.cap : 0, partial: true };
  };

  // 給定範圍彙總可用列：日粒度優先用日資料，超出日資料範圍退回月列；季/年逐月，當月改用「到 to」日彙總避免整月未來預估
  rowsForRange = (gran, from, to) => {
    const curMonth = moment().startOf("month");
    if (gran === "month") {
      const days = this.state.dayItems.filter((it) => it.m.isBetween(from, to, "day", "[]"));
      if (days.length) return days;
      return this.state.monthItems.filter((it) => it.m.isBetween(from, to, "month", "[]"));
    }
    const out = [];
    this.state.monthItems.filter((it) => it.m.isBetween(from, to, "month", "[]")).forEach((mr) => {
      if (mr.m.isSame(curMonth, "month")) {
        const cm = this.state.dayItems.filter((it) => it.m.isSame(curMonth, "month") && it.m.isSameOrBefore(to, "day"));
        if (cm.length) { out.push(this.synthMonth(curMonth, cm)); return; }
      }
      out.push(mr);
    });
    return out;
  };

  // 本期各桶列 (依 offset 決定檢視期)：供 KPI 彙總；逐桶明細改走 trendBuckets
  curRows = (gran) => { const { from, to } = this.viewRange(gran, this.state.offset); return this.rowsForRange(gran, from, to); };
  range = (gran) => this.viewRange(gran, this.state.offset); // 供 render / fetchBreakdown

  // 對比區間標籤：一律用「去年同期完整期間 (與全期對比一致)」的實際區間，與 KPI 卡對比口徑一致
  prevRangeLabel = (gran) => {
    const { from, to } = this.kpiCompareRange(gran);
    return `${from.format("YYYY/MM/DD")}–${to.format("MM/DD")}`;
  };

  periodTitle = () => {
    const o = this.state.offset, base = { month: "月", quarter: "季", year: "年" }[this.state.gran];
    if (o === 0) return `本${base}`;
    if (o === -1) return `上${base}`;
    if (o === 1) return `下${base}`;
    return `${Math.abs(o)} ${base}${o < 0 ? "前" : "後"}`;
  };

  agg = (items) => {
    const s = items.reduce(
      (a, it) => {
        a.nights += it.use_qnt;
        a.cap += it.total_rooms;
        a.rent += it.rent_amt;
        a.rs += it.rs_amt;
        a.rev += it.total_revenue;
        return a;
      },
      { nights: 0, cap: 0, rent: 0, rs: 0, rev: 0 }
    );
    return {
      occ: s.cap ? (s.nights / s.cap) * 100 : 0,
      adr: s.nights ? (s.rent + s.rs) / s.nights : 0,
      revpar: s.cap ? s.rev / s.cap : 0,
      revenue: s.rev,
      nights: s.nights,
    };
  };

  // 對比期範圍 (標題用)：與 fullCompareRange 同口徑 (全期回推一年/上週)，直接委派避免兩份重複實作
  kpiCompareRange = (gran) => this.fullCompareRange(gran);

  // 趨勢/明細的對比期範圍：整段回推 cmpYears 年 (日曆對齊，與 KPI 卡/目標達成同口徑)，
  // 對照本期趨勢「完整範圍」(含本期未來桶)，避免本期未來桶因期間對齊差異而斷線。
  // 桶怎麼配對見 cmpMatcher —— 日比同日期、週比第幾週、月比同月，都是桶的日曆身分對桶的日曆身分。
  trendCmpRange = (gran) => {
    const anchor = moment().add(this.state.offset, this.stepOf(gran));
    const b = this.cmpBack();
    const unit = this.unitFor(gran);
    // 對齊到完整 unit 邊界 (含跨月的頭尾週) 再回推，確保對照桶數與本期一致、不會少一週
    return {
      from: anchor.clone().startOf(this.unitOf(gran)).startOf(unit).subtract(b.n, b.u),
      to: anchor.clone().endOf(this.unitOf(gran)).endOf(unit).subtract(b.n, b.u),
    };
  };

  fetchBreakdown = async () => {
    const f = (m) => m.format("YYYY/MM/DD");
    const { from, to } = this.range(this.state.gran);
    const rcmp = this.realizedCompareRange(this.state.gran); // 去年同進度 (可能 null)
    const fcmp = this.fullCompareRange(this.state.gran);     // 去年全期最終
    // 走勢/明細/前瞻的來源粒度：桶為週時要日資料再分桶，故只有年 (月桶) 用 month
    const tu = this.unitFor(this.state.gran) === "month" ? "month" : "day";
    const tcmp = this.trendCmpRange(this.state.gran); // 走勢/明細的對比窗口 (整段回推 cmpYears 年)
    // 前瞻窗口回推一年 → 抓「去年同期實際」逐桶
    const fr = this.forecastRange();
    // 日/週桶前瞻的「去年同期」用 364 天 (52 週) 對齊星期;月桶保留 -1 年 (日曆月對齊)
    const fcBack = fr.unit === "month" ? { n: 1, u: "year" } : { n: 364, u: "days" };
    const fcFrom = fr.start.clone().subtract(fcBack.n, fcBack.u), fcTo = fr.to.clone().subtract(fcBack.n, fcBack.u);
    // 請求版本守衛：快速切換粒度/期間時，僅讓「最新一次」的回應寫入 state，避免舊回應覆蓋
    const reqId = (this._reqId = (this._reqId || 0) + 1);
    this.fetchFeedback();
    this.fetchFeedbackNeeds();
    this.setState({ breakdownLoading: true, breakdown: null, breakdownCmp: null, kpiCur: null, kpiCmp: null, kpiCmpFull: null, trendCmp: [], trendCur: [], forecastCmp: [], pace: [], paceBand: [], paceBandLoading: !!this.paceUnitFor(this.state.gran) && this.state.offset >= 0, velocity: [], pickup: [], roomPickup: [],
      velocityLoading: this.state.offset >= 0, pickupLoading: this.state.offset >= 0 });
    // 訂房增速：與下面那批阻塞請求無相依 (只用到本地算出的 fr)，放在 await 之前發出，
    // 否則要等 8 個請求全部回來才開始排隊，卡片會空等一輪。
    this.fetchVelocity(reqId, fr);
    // 前瞻逐日在手 (團體/散客分層)：同樣不依賴那批阻塞請求，一起先發出
    this.fetchPace(reqId, fr);
    // 各房型近期 pickup (非阻塞，本期 [from,to]、過去期無意義故略過)
    if (this.state.offset >= 0) {
      PmsAPI.ops.pacePickupByRoom(f(from), f(to), 30)
        .then((r) => { if (reqId === this._reqId) this.setState({ roomPickup: r.data.items || [] }); })
        .catch(() => {});
    }
    try {
      const res = await Promise.all([
        PmsAPI.ops.breakdown(f(from), f(to)),
        PmsAPI.ops.breakdown(f(fcmp.from), f(fcmp.to)),
        PmsAPI.ops.kpi(f(from), f(to)),
        PmsAPI.ops.kpi(f(fcmp.from), f(fcmp.to)),                         // 全期 vs 去年最終
        rcmp ? PmsAPI.ops.kpi(f(rcmp.from), f(rcmp.to)) : Promise.resolve({ data: null }), // 去年同進度
        PmsAPI.ops.series(f(tcmp.from), f(tcmp.to), tu),   // 走勢/明細的對比期
        PmsAPI.ops.series(f(from), f(to), tu),             // 走勢/明細的本期 (本地日資料只到 +5 個月，往後翻期會缺)
        PmsAPI.ops.series(f(fcFrom), f(fcTo), tu),         // 前瞻的「去年同期實際」
      ]);
      if (reqId !== this._reqId) return; // 已有更新的請求發出，丟棄這次過時的回應
      const [bd, bdc, kc, kpFull, kpReal, tc, tcur, fcc] = res;
      const keyFmt = tu === "month" ? "YYYY/MM" : "YYYY/MM/DD";
      const norm = (r) => this.norm(r.data.items, keyFmt, Number(r.data.capacity) || 0);
      this.setState({
        breakdown: bd.data, breakdownCmp: bdc.data,
        kpiCur: kc.data, kpiCmp: kpReal.data, kpiCmpFull: kpFull.data,
        trendCmp: norm(tc), trendCur: norm(tcur), forecastCmp: norm(fcc),
      });
      // 訂房曲線正常帶：各粒度皆適用；因多年回推仍需數秒，改「非阻塞」單獨抓，
      // 不擋整片儀表板載入，回來再更新前瞻圖 (後端已快取，之後即時)
      this.fetchPaceBand(reqId, fr);
    } catch (e) {
      if (reqId === this._reqId) this.setState({ error: this.errMsg(e) });
    } finally {
      if (reqId === this._reqId) this.setState({ breakdownLoading: false });
    }
  };

  /** 正常帶回推的年數。paceBand 與這裡算對照樣本日期時必須一致，否則標示的樣本數對不上。 */
  static BAND_YEARS = 5;

  // 前瞻粒度 → pace-band 分桶單位，與前瞻圖的桶單位一致 (月=日 / 季=週 / 年=月)
  paceUnitFor = (gran) => ({ month: "day", quarter: "week", year: "month" }[gran]);
  // 正常帶回傳的桶鍵格式：月桶=YYYY/MM、日/週桶=YYYY/MM/DD (週桶鍵為該週起日)。
  // 必須跟著 pace 分桶單位走而非粒度，否則年粒度會拿日格式去查月桶而全數落空
  paceKeyFmt = (gran) => (this.paceUnitFor(gran) === "month" ? "YYYY/MM" : "YYYY/MM/DD");
  // 遠期桶的正常帶退化 (近年同進度幾乎還沒開賣，P75 < 1%) 時不給超前/落後結論：
  // 實測明年 1 月在手 0.35% (11 間夜) vs 正常帶 0–0.1%，會被判「超前」塗成綠色 —— 那只是雜訊
  paceStatus = (p, occ) => (!p ? null : p.hi < 1 ? "尚早" : occ > p.hi ? "超前" : occ < p.lo ? "落後" : "正常");

  // 非阻塞抓訂房曲線正常帶：不擋主載入，回來再更新前瞻圖 (reqId 守衛避免過時回應覆蓋)
  // 本期與未來期 (offset>=0) 前瞻窗口皆為未來，可與「同提前天數」as-of 今天對照；
  // 過去期 (offset<0) 前瞻窗口含已實現日期，pace 無意義，沿用去年最終線。
  fetchPaceBand = (reqId, fr) => {
    const unit = this.paceUnitFor(this.state.gran);
    if (!unit || this.state.offset < 0) { if (reqId === this._reqId) this.setState({ paceBandLoading: false }); return; }
    const f = (m) => m.format("YYYY/MM/DD");
    const years = OpsDashboard.BAND_YEARS;
    PmsAPI.ops.paceBand(f(fr.start), f(fr.to), f(moment()), years, unit)
      .then((r) => { if (reqId === this._reqId) this.setState({ paceBand: r.data.items || [] }); })
      .catch(() => {})
      .finally(() => { if (reqId === this._reqId) this.setState({ paceBandLoading: false }); });
    // 對照樣本落在哪幾年由 364×k 決定，一次把那段期間的國定假日抓齊 (holidayCalendar 以年度快取，
    // 同一年只會發一次請求)。失敗時維持空 Set —— 標不出來就不標，不影響正常帶本身。
    const range = sampleRange(f(fr.start), f(fr.to), years);
    if (range) {
      fetchSpecialDates(range.from, range.to)
        .then((set) => { if (reqId === this._reqId) this.setState({ bandHolidays: set }); })
        .catch(() => {});
    }
  };

  /**
   * 非阻塞抓前瞻逐日在手 (含團體/散客分層)。與 fetchPaceBand 同一個窗口、同一個 as-of 還原口徑 ——
   * 前瞻柱改吃它而不是本地 dayItems (RVUSE_DT)，柱與正常帶才是同一把尺。兩者實測多數日只差 0~1 間，
   * 但落在低基數的日子相對誤差就明顯 (如 2026/09/14：RVUSE_DT 22 間 vs as-of 還原 19 間)。
   * 分桶單位跟著前瞻圖走 (月=日 / 季=週 / 年=月)，由後端在 SQL 裡分桶 ——
   * 逐日回傳再讓前端加總行不通：那需要對年窗口要 366 天的逐日資料，實測 65.9 秒，過不了 nginx 的 60 秒。
   */
  fetchPace = (reqId, fr) => {
    const unit = this.paceUnitFor(this.state.gran);
    if (!unit || this.state.offset < 0) { return; }
    const f = (m) => m.format("YYYY/MM/DD");
    PmsAPI.ops.pace(f(fr.start), f(fr.to), f(moment()), unit)
      .then((r) => { if (reqId === this._reqId) this.setState({ pace: r.data.items || [] }); })
      .catch(() => {});
  };

  // 非阻塞抓訂房增速：窗口 = 前瞻窗口 (未來住用)，近 30 訂房日的每日淨訂房。過去期 (offset<0) 不適用。
  fetchVelocity = (reqId, fr) => {
    if (this.state.offset < 0) { if (reqId === this._reqId) this.setState({ velocityLoading: false, pickupLoading: false }); return; }
    const f = (m) => m.format("YYYY/MM/DD");
    // 訂房增速摘要 (依訂房日的動能，供 hot/cold 判定與標題徽章)；回看天數依粒度 (聚合後桶數才合理)
    PmsAPI.ops.paceVelocity(f(fr.start), f(fr.to), this.velDays())
      .then((r) => { if (reqId === this._reqId) this.setState({ velocity: r.data.items || [] }); })
      .catch(() => {})
      .finally(() => { if (reqId === this._reqId) this.setState({ velocityLoading: false }); });
    // 訂房增速圖 (依未來住用日的近期淨 pickup，與前瞻同 x 軸)
    PmsAPI.ops.pacePickup(f(fr.start), f(fr.to), 30)
      .then((r) => { if (reqId === this._reqId) this.setState({ pickup: r.data.items || [] }); })
      .catch(() => {})
      .finally(() => { if (reqId === this._reqId) this.setState({ pickupLoading: false }); });
  };

  // 住客回饋：不擋主載入，回來再更新。儀表板取一次餵給住客體驗卡與下方的完整摘要卡，
  // 兩處各自呼叫會出現一邊已載入、另一邊顯示錯誤的不一致畫面。
  //
  // fetchBreakdown 的進入點不只 gran/offset 變動 (例如 setCmpYears 改比較基準年份)，
  // 但回饋的期間只由 gran/from/to 決定 —— cmpYears 不影響。用 _feedbackKey 記住上次
  // 已發出請求的 gran/from/to，相同就跳過，避免多閃一次 Spin、多打一次 API。
  // 跳過時不可把 feedback 重設為 null，否則畫面仍會閃一下。
  //
  // 用自己的 _feedbackReqId (而非 fetchBreakdown 傳入的 reqId) 判斷回應是否過時：
  // fetchBreakdown 的 reqId 每次呼叫都會遞增 (即使這裡跳過)，若沿用它，跳過時仍在途的
  // 前一次請求回來時會因 reqId 已被後面的呼叫超前而被誤判為過時、遭丟棄，導致 Spin 卡住。
  fetchFeedback = () => {
    const fr = this.range(this.state.gran);
    const from = fr.from.format("YYYY/MM/DD"), to = fr.to.format("YYYY/MM/DD");
    const key = `${this.state.gran}|${from}|${to}`;
    if (key === this._feedbackKey) return;
    this._feedbackKey = key;
    const feedbackReqId = (this._feedbackReqId = (this._feedbackReqId || 0) + 1);
    this.setState({ feedback: null, feedbackError: false });
    PmsAPI.ops
      .feedbackSummary(this.state.gran, from, to)
      .then((r) => { if (feedbackReqId === this._feedbackReqId) this.setState({ feedback: r.data }); })
      .catch(() => { if (feedbackReqId === this._feedbackReqId) this.setState({ feedbackError: true }); });
  };

  // 住客需求有自己的 key：去年同期區間會隨對比年數 (cmpYears) 改變，這只影響需求排行、
  // 不影響 AI 摘要；共用 fetchFeedback 的 key 會讓改對比年數時摘要也被清空重取而閃 Spin。
  fetchFeedbackNeeds = () => {
    const fr = this.range(this.state.gran);
    const from = fr.from.format("YYYY/MM/DD"), to = fr.to.format("YYYY/MM/DD");
    // 住客需求的「去年同期」用「已過進度」的對比區間 (realized)，不是 KPI 卡的全期：
    // 回饋只有過去的資料，不像 OTB 訂房有未來的量。本期還沒走完時若拿去年整期比，
    // 月初幾乎每一項都會顯示 ↓。整期都在未來時為 null，此時不帶 prev_*，後端回 prev_count null、畫面顯示 "-"。
    const pr = this.realizedCompareRange(this.state.gran);
    const prevFrom = pr ? pr.from.format("YYYY/MM/DD") : null, prevTo = pr ? pr.to.format("YYYY/MM/DD") : null;
    const key = `${this.state.gran}|${from}|${to}|${prevFrom}|${prevTo}`;
    if (key === this._feedbackNeedsKey) return;
    this._feedbackNeedsKey = key;
    const reqId = (this._feedbackNeedsReqId = (this._feedbackNeedsReqId || 0) + 1);
    this.setState({ feedbackNeeds: null, feedbackNeedsError: false });
    PmsAPI.ops
      .feedbackNeeds(from, to, prevFrom, prevTo)
      .then((r) => { if (reqId === this._feedbackNeedsReqId) this.setState({ feedbackNeeds: r.data }); })
      .catch((e) => {
        if (reqId !== this._feedbackNeedsReqId) return;
        // 404：前端先於後端上線，功能等同關閉，不該跳出錯誤 (上線期間畫面應維持原樣)
        if (e && e.response && e.response.status === 404) this.setState({ feedbackNeeds: { enabled: false } });
        else this.setState({ feedbackNeedsError: true });
      });
  };

  changeGran = (e) => {
    // offset 歸零是刻意的：換粒度後「上一期」指的已經不是同一段時間
    try { localStorage.setItem("ops.gran", e.target.value); } catch (err) { /* 忽略儲存失敗 */ }
    this.setState({ gran: e.target.value, offset: 0 }, this.fetchBreakdown);
  };

  shift = (delta) => {
    this.setState((s) => ({ offset: s.offset + delta }), this.fetchBreakdown);
  };

  prevLabel = () => `${this.cmpYearWord()}同期`;
  // 日期 + 星期 (以空白間隔)，六/日以紅色明顯標示
  dayWithWd = (m, fmt) => (
    <span style={m.isoWeekday() >= 6 ? { color: WEEKEND_COLOR, fontWeight: 600 } : undefined}>{m.format(fmt)} ({wdc(m)})</span>
  );
  // 房型維度上期對照小字：上期值 + 相對變化 (綠升紅降)
  rtCmp = (curV, prevV, fmt) => {
    if (prevV == null || prevV === 0) return <div style={{ fontSize: FONT.caption, color: FAINT_COLOR }}>上期 —</div>;
    const d = curV == null ? null : ((curV - prevV) / prevV) * 100;
    const flat = d != null && Math.abs(d) < 1;
    const col = d == null || flat ? MUTED_COLOR : d > 0 ? "#3f8600" : "#cf1322";
    return (
      <div style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>
        上期 {fmt(prevV)}
        {d != null && <span style={{ color: col, marginLeft: 4 }}>{flat ? "持平" : `${d > 0 ? "▲" : "▼"}${Math.abs(d).toFixed(0)}%`}</span>}
      </div>
    );
  };

  // ---- 衍生 ----
  delta = (cur, prev) => (prev ? ((cur - prev) / prev) * 100 : null);

  statusWord = (d, band = 2) => {
    if (d === null) return { word: "—", color: MUTED_COLOR };
    if (d > band) return { word: "優於對比", color: "#3f8600" };
    if (d < -band) return { word: "低於對比", color: "#cf1322" };
    return { word: "與對比相當", color: "#597ef7" };
  };

  extremes = (items, key = "occupancy_rate") => {
    if (!items.length) return { max: null, min: null };
    let max = items[0], min = items[0];
    items.forEach((it) => {
      if (it[key] > max[key]) max = it;
      if (it[key] < min[key]) min = it;
    });
    return { max, min };
  };

  // 各粒度的時間單位：月→日 (看得到平日與週末的起伏) / 季→週 (逐日 92 點過雜，逐週才看得出哪一週在掉) / 年→月。
  // 期間走勢、前瞻、訂房增速三張圖共用同一單位，同一個檢視期內的 x 軸粒度一致
  unitFor = (gran) => ({ month: "day", quarter: "isoWeek", year: "month" }[gran]);
  bucketKeyFmt = (unit) => (unit === "day" ? "YYYY-MM-DD" : unit === "isoWeek" ? "GGGG-[W]WW" : "YYYY-MM");
  bucketLabel = (unit, m) => (unit === "day" ? fmtDay(m) : unit === "isoWeek" ? m.format("M/D") + "週" : m.format("YYYY/MM"));
  // 訂房增速「依訂房日」回看的訂房日天數：粒度越粗回看越長，聚合後桶數才合理 (後端上限 120)
  velDays = () => ({ month: 30, quarter: 84, year: 120 }[this.state.gran] || 30);

  // 依 unit 把日/月資料分桶，[from,to] 內每桶都建立(缺資料補 0)
  // srcOverride：指定資料來源 (對比期趨勢用後端 series，本地無去年資料)；未給則用本地日/月資料
  bucketize = (unit, from, to, srcOverride) => {
    const src = srcOverride || (unit === "month" ? this.state.monthItems : this.state.dayItems);
    const step = unit === "isoWeek" ? "week" : unit;
    const fmt = this.bucketKeyFmt(unit);
    const map = new Map();
    let cur = from.clone().startOf(unit);
    const end = to.clone().endOf("day");
    let guard = 0;
    while (cur.isSameOrBefore(end) && guard++ < 500) {
      const k = cur.format(fmt);
      if (!map.has(k)) map.set(k, { start: cur.clone(), use: 0, cap: 0, rent: 0, rs: 0, rev: 0 });
      cur = cur.clone().add(1, step);
    }
    src.forEach((it) => {
      if (it.m.isBefore(from, "day") || it.m.isAfter(to, "day")) return;
      const k = it.m.clone().startOf(unit).format(fmt);
      let b = map.get(k);
      if (!b) { b = { start: it.m.clone().startOf(unit), use: 0, cap: 0, rent: 0, rs: 0, rev: 0 }; map.set(k, b); }
      b.use += it.use_qnt; b.cap += it.total_rooms; b.rent += it.rent_amt; b.rs += it.rs_amt; b.rev += it.total_revenue;
    });
    return [...map.values()].sort((a, b) => a.start.valueOf() - b.start.valueOf()).map((b) => ({
      m: b.start, use: b.use, cap: b.cap, rent: b.rent, rs: b.rs, rev: b.rev,
      occ: b.cap ? (b.use / b.cap) * 100 : 0, occupancy_rate: b.cap ? (b.use / b.cap) * 100 : 0,
      adr: b.use ? b.rent / b.use : 0, // 純房租 ADR (與 KPI 一致)
      label: this.bucketLabel(unit, b.start), weekend: unit === "day" && b.start.isoWeekday() >= 6,
    }));
  };

  // 趨勢：檢視期完整範圍(含未來在手)依走勢 unit 分桶。trendCur 有值時優先 (季逐日，本地日資料涵蓋不到)
  trendBuckets = (gran) => {
    const anchor = moment().add(this.state.offset, this.stepOf(gran));
    const from = anchor.clone().startOf(this.unitOf(gran));
    const to = anchor.clone().endOf(this.unitOf(gran));
    return this.bucketize(this.unitFor(gran), from, to, this.state.trendCur);
  };

  // 本期桶 → 對比期桶的配對：比對「桶的日曆身分」，走勢圖與明細對照共用同一份規則。
  //  日桶  → 同日曆日期 (2026/08/31 ↔ 2025/08/31)：固定日期的節慶 (10/10、1/1、跨年) 才對得上
  //  週桶  → 一年內的第幾週 (ISO 週號)：週界落點逐年不同，比日期會切半週
  //  月桶  → 同月份
  // 對比年沒有對應桶時 (如今年有第 53 週而對比年只有 52 週) 回 null，表上顯示「—」而非硬湊一個。
  cmpMatcher = (gran, cmpBuckets) => {
    const unit = this.unitFor(gran);
    const key = unit === "day" ? (m) => m.format("MM/DD") : unit === "isoWeek" ? (m) => m.isoWeek() : (m) => m.format("MM");
    const map = new Map(cmpBuckets.map((c) => [key(c.m), c]));
    return (b) => map.get(key(b.m)) || null;
  };

  // 對比期趨勢桶：以後端 series (去年同期) 依趨勢 unit 重新分桶，
  // 與本期趨勢同 unit、同起點對齊。改用後端資料以修正月粒度本地無去年日資料而對比線全空的問題。
  trendCmpBuckets = (gran) => {
    const { from, to } = this.trendCmpRange(gran); // 完整期間去年同期，含未來週不斷線
    return this.bucketize(this.unitFor(gran), from, to, this.state.trendCmp);
  };

  // 前瞻 OTB 範圍：未來範圍依同一 unit 分桶 (月→日×90 / 季→週×13 / 年→月×12)
  // 抽出範圍供 forecast() 與「去年同期實際」對照 (forecastCmp) 共用
  forecastRange = () => {
    const gran = this.state.gran;
    const unit = this.unitFor(gran);
    const step = unit === "isoWeek" ? "week" : unit;
    // 前瞻從「今天」與「檢視期起點」兩者較晚者起算，讓本期尚未到的未來日 (如本週剩餘的 7/18–19) 也納入；
    // 未來期則從該期起點起算。以 unit 邊界對齊。
    const anchor = moment().add(this.state.offset, this.stepOf(gran));
    const start = moment.max(moment(), anchor.clone().startOf(this.unitOf(gran))).startOf(unit);
    const n = { month: 90, quarter: 13, year: 12 }[gran];
    return { unit, unitName: { day: "天", isoWeek: "週", month: "月" }[unit], step, start, to: start.clone().add(n, step), n };
  };
  /**
   * 前瞻分桶。一輪 render 會從好幾個地方問它 (render 本身、otbOption、bucketPickup 的預設參數、
   * forecastRows)，每次都重跑一遍 bucketize 是白花的 —— 而且每次 setState 都會再來一輪，
   * 包含視窗縮放的 narrow 旗標與每支非阻塞請求回來。
   *
   * 記憶化的 key 用 state 的 identity (陣列都是整個換掉的) 再加上今天的日期 ——
   * forecastRange() 的起點是 max(今天, 檢視期起點)，跨過午夜後不把日期算進去會沿用昨天的窗口。
   */
  forecast = () => {
    const s = this.state;
    const key = [s.gran, s.offset, s.dayItems, s.monthItems, moment().format("YYYY/MM/DD")];
    if (this._fcMemo && this._fcMemo.key.every((v, i) => v === key[i])) return this._fcMemo.value;
    const { unit, unitName, start, to, n } = this.forecastRange();
    const value = { unit: unitName, buckets: this.bucketize(unit, start, to).slice(0, n) };
    this._fcMemo = { key, value };
    return value;
  };
  // 前瞻對照：未來窗口回推一年的「去年同期實際」住房率 (後端 series，逐桶與 forecast 同位置對齊)
  forecastCmpBuckets = () => {
    const { unit, start, to, n } = this.forecastRange();
    // 與 fetchBreakdown 的 forecastCmp 取值一致：日/週用 364 天對齊星期,月用 -1 年
    const b = unit === "month" ? { n: 1, u: "year" } : { n: 364, u: "days" };
    return this.bucketize(unit, start.clone().subtract(b.n, b.u), to.clone().subtract(b.n, b.u), this.state.forecastCmp).slice(0, n);
  };

  // 度量 (營收 rev / 房晚 nights)
  mVal = (r) => (this.state.breakdownMetric === "rev" ? Number(r.rev) || 0 : Number(r.nights) || 0);
  mFmt = (v) => (this.state.breakdownMetric === "rev" ? "NT$" + Math.round(v).toLocaleString() : Math.round(v).toLocaleString() + " 夜");
  mLabel = () => (this.state.breakdownMetric === "rev" ? "營收" : "房晚");
  metricSwitch = () => (
    <Radio.Group size="small" aria-label="顯示度量" value={this.state.breakdownMetric} onChange={(e) => this.setPersisted("breakdownMetric", "ops.breakdownMetric", e.target.value)}>
      <Radio.Button value="rev">營收</Radio.Button>
      <Radio.Button value="nights">房晚</Radio.Button>
    </Radio.Group>
  );

  // ADR (純房租) = 房租營收 / 房晚，與 KPI 的 ADR 口徑一致；與營收/房晚切換無關
  rowAdr = (nights, rev) => (nights > 0 ? Math.round(rev / nights) : 0);

  topN = (list, n = 6) => {
    const arr = (list || []).slice().sort((a, b) => this.mVal(b) - this.mVal(a));
    const total = arr.reduce((s, c) => s + this.mVal(c), 0) || 1;
    // 佔比的分母是全部項目 (含併進「其他」的)，與卡片右上的合計同一個數；跟著度量切換
    const share = (v) => Math.round((v / total) * 1000) / 10;
    // r_nights/o_nights/r_rev/o_rev：Oracle 原生列為字串，統一以 Number(x)||0 轉數字，供 splitBar 拆已實現/在手。
    const rows = arr.slice(0, n).map((c) => {
      const nights = Number(c.nights) || 0, rev = Number(c.rev) || 0;
      return {
        name: (c.nam || c.rate_cod || "").trim() || "未分類", value: this.mVal(c), share: share(this.mVal(c)), nights, rev, adr: this.rowAdr(nights, rev),
        r_nights: Number(c.r_nights) || 0, o_nights: Number(c.o_nights) || 0, r_rev: Number(c.r_rev) || 0, o_rev: Number(c.o_rev) || 0,
      };
    });
    const rest = arr.slice(n);
    const restV = rest.reduce((s, c) => s + this.mVal(c), 0);
    if (restV > 0) {
      const rn = rest.reduce((s, c) => s + (Number(c.nights) || 0), 0), rr = rest.reduce((s, c) => s + (Number(c.rev) || 0), 0);
      const ron = rest.reduce((s, c) => s + (Number(c.r_nights) || 0), 0), oon = rest.reduce((s, c) => s + (Number(c.o_nights) || 0), 0);
      const rorv = rest.reduce((s, c) => s + (Number(c.r_rev) || 0), 0), oorv = rest.reduce((s, c) => s + (Number(c.o_rev) || 0), 0);
      rows.push({ name: "其他", value: restV, share: share(restV), nights: rn, rev: rr, adr: this.rowAdr(rn, rr), r_nights: ron, o_nights: oon, r_rev: rorv, o_rev: oorv });
    }
    return { rows, total };
  };
  sourceTop = () => this.topN((this.state.breakdown && this.state.breakdown.source) || [], 6);
  planTop = () => this.topN((this.state.breakdown && this.state.breakdown.plan) || [], 6);

  // 通路類型決策：依 OTA / 直訂 房晚佔比
  channelDecision = () => {
    const ct = (this.state.breakdown && this.state.breakdown.channelType) || [];
    if (!ct.length) return null;
    const totalN = ct.reduce((s, c) => s + Number(c.nights), 0) || 1;
    const sumBy = (kw) => ct.filter((c) => kw.some((k) => (c.nam || "").indexOf(k) >= 0)).reduce((s, c) => s + Number(c.nights), 0);
    const otaPct = (sumBy(["網路旅行社"]) / totalN) * 100;
    const dPct = (sumBy(["官網", "電話"]) / totalN) * 100;
    if (otaPct >= 50) return { tone: "warn", text: `OTA 依賴度偏高 (${otaPct.toFixed(0)}%)，強化官網會員價/CRM 提升直訂、降低佣金成本；扣佣後淨 ADR 常低於直訂。` };
    if (dPct >= 40) return { tone: "pos", text: `直訂佔比健康 (${dPct.toFixed(0)}%)，維持通路組合、持續會員經營。` };
    return { tone: "info", text: `通路組合尚均衡 (OTA ${otaPct.toFixed(0)}% / 直訂 ${dPct.toFixed(0)}%)，持續觀察消長。` };
  };

  // 訂房來源決策：依來源集中度 (排除直客的最大單一來源佔比)
  sourceDecision = () => {
    const src = (this.state.breakdown && this.state.breakdown.source) || [];
    if (!src.length) return null;
    const totalN = src.reduce((s, c) => s + Number(c.nights), 0) || 1;
    const agents = src.filter((c) => (c.nam || "").indexOf("直客") < 0).slice().sort((a, b) => b.nights - a.nights);
    const top = agents[0];
    if (top && (top.nights / totalN) * 100 >= 25) {
      return { tone: "warn", text: `來源集中於「${(top.nam || "").trim()}」(${((top.nights / totalN) * 100).toFixed(0)}%)，留意佣金與議價風險，宜分散並強化直訂。` };
    }
    return { tone: "info", text: `訂房來源分散，單一通路風險較低；持續經營直訂會員。` };
  };

  decisionBox = (dec) => (dec ? (
    <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "flex-start", background: "#fafafa", borderRadius: 6, padding: "8px 10px" }}>
      <Tag color={dec.tone === "pos" ? "green" : dec.tone === "warn" ? "orange" : "blue"} style={{ marginTop: 1 }}>建議</Tag>
      <span style={{ color: "#595959", flex: 1 }}>{dec.text}</span>
    </div>
  ) : null);

  // 通路類型：SOURCE_TYP 名稱分成 直訂/OTA/旅行社/現場，看真實通路依賴度
  channelTypeAgg = () => {
    const ct = (this.state.breakdown && this.state.breakdown.channelType) || [];
    const zero = () => ({ nights: 0, rev: 0, r_nights: 0, o_nights: 0, r_rev: 0, o_rev: 0 });
    const map = { OTA: zero(), "直訂": zero(), "旅行社": zero(), "現場": zero(), "其他": zero() };
    ct.forEach((c) => {
      const n = c.nam || "";
      let k = "其他";
      if (n.indexOf("網路旅行社") >= 0) k = "OTA";
      else if (n.indexOf("官網") >= 0 || n.indexOf("電話") >= 0) k = "直訂";
      else if (n.indexOf("旅行社") >= 0) k = "旅行社";
      else if (n.toLowerCase().indexOf("walk") >= 0 || n.indexOf("現場") >= 0) k = "現場";
      // r_nights/o_nights/r_rev/o_rev：Oracle 原生列為字串，統一以 Number(x)||0 轉數字。
      map[k].nights += Number(c.nights) || 0;
      map[k].rev += Number(c.rev) || 0;
      map[k].r_nights += Number(c.r_nights) || 0;
      map[k].o_nights += Number(c.o_nights) || 0;
      map[k].r_rev += Number(c.r_rev) || 0;
      map[k].o_rev += Number(c.o_rev) || 0;
    });
    const rows = ["OTA", "直訂", "旅行社", "現場", "其他"].filter((k) => this.mVal(map[k]) > 0).map((k) => {
      const m = map[k];
      return { name: k, value: this.mVal(m), nights: m.nights, rev: m.rev, adr: this.rowAdr(m.nights, m.rev), r_nights: m.r_nights, o_nights: m.o_nights, r_rev: m.r_rev, o_rev: m.o_rev };
    });
    const total = rows.reduce((s, r) => s + r.value, 0) || 1;
    return { rows, total };
  };

  // 本期是否為「至今」的部分期間 (offset 0)：累計型指標不宜與完整上期比
  isPartial = () => this.state.offset === 0;

  // ---- echarts ----
  trendOption = () => {
    const buckets = this.trendBuckets(this.state.gran);
    const cmp = this.trendCmpBuckets(this.state.gran); // 對比期桶
    const cmpFor = this.cmpMatcher(this.state.gran, cmp); // 日比同日期 / 週比第幾週 / 月比同月
    const avg = this.agg(this.curRows(this.state.gran)).occ; // 期間均值 (與 KPI 一致)
    const today0 = moment().startOf("day");
    const bUnit = this.unitFor(this.state.gran); // 走勢桶單位：週/月/季→day、年→month
    // 今天所在桶 (或第一個未來桶) 索引；找不到 (全過去/全未來) 則 -1
    const todayIdx = buckets.findIndex((b) => b.m.clone().endOf(bUnit).isSameOrAfter(today0));
    const otbStartIdx = buckets.findIndex((b) => b.m.clone().startOf(bUnit).isSameOrAfter(today0));
    const many = buckets.length > 8;
    const cmpName = this.prevLabel() + "住房率";
    const cmpAdrName = this.prevLabel() + "平均房價";
    return {
      tooltip: { trigger: "axis" },
      // containLabel 讓兩側 Y 軸標籤都被容納，避免軸被切掉；grid.top 需容納圖例 (窄寬可能換兩行) 避免圖例壓到線
      grid: { top: this.state.narrow ? 78 : 58, left: 6, right: 10, bottom: many ? 18 : 6, containLabel: true },
      legend: { top: 4, left: "center", itemGap: 12, itemWidth: 16, textStyle: { fontSize: 11 }, data: ["住房率", cmpName, "平均房價", cmpAdrName] },
      xAxis: { type: "category", boundaryGap: true, data: buckets.map((b) => b.label), axisLabel: { fontSize: 10, interval: 0, rotate: many ? 40 : 0, hideOverlap: true, formatter: (v) => (isWeekendLabel(v) ? `{wk|${v}}` : v), rich: { wk: { color: WEEKEND_COLOR, fontWeight: "bold", fontSize: 10 } } } },
      yAxis: [
        { type: "value", min: 0, max: 100, axisLabel: { formatter: "{value}%" }, splitLine: { lineStyle: { color: "#f0f0f0" } } },
        { type: "value", min: 0, axisLabel: { formatter: kAxis }, splitLine: { show: false } },
      ],
      series: [
        {
          name: "住房率", type: "line", smooth: true, connectNulls: true, areaStyle: { opacity: 0.08 },
          // 點多時 (季逐日約 92 點) 關掉圓點，否則圓點連成一片、看不出線的形狀；hover 仍照常出 tooltip
          symbolSize: 6, showSymbol: buckets.length <= 35, itemStyle: { color: "#1890ff" },
          data: buckets.map((b) => Number(b.occ.toFixed(1))),
          markLine: { silent: true, symbol: "none", data: [
            { yAxis: Number(avg.toFixed(1)), label: { formatter: `期間均值 ${avg.toFixed(1)}%`, position: "insideStartTop", fontSize: 10, color: "#faad14" }, lineStyle: { type: "dashed", color: "#faad14" } },
            ...(this.state.offset === 0 && todayIdx >= 0 ? [{ xAxis: buckets[todayIdx].label, label: { formatter: "今天", fontSize: 10, color: "#fa8c16" }, lineStyle: { color: "#fa8c16", type: "dashed", width: 1 } }] : []),
          ] },
          markArea: otbStartIdx >= 0 ? {
            silent: true, itemStyle: { color: "rgba(250,173,20,0.06)" },
            label: { position: "insideBottom", color: "#fa8c16", fontSize: 10, distance: 6 }, // 「在手」置於區塊底部，避開頂部圖例/今天標籤
            data: [[{ xAxis: buckets[otbStartIdx].label, name: "在手" }, { xAxis: buckets[buckets.length - 1].label }]],
          } : undefined,
        },
        {
          name: cmpName, type: "line", smooth: true, symbol: "none",
          lineStyle: { type: "dashed", color: METRIC_COLOR.occ, width: 1.5, opacity: 0.7 }, itemStyle: { color: METRIC_COLOR.occ },
          data: buckets.map((b, i) => { const c = cmpFor(b, i); return c && c.cap ? Number(c.occ.toFixed(1)) : null; }),
        },
        { name: "平均房價", type: "line", yAxisIndex: 1, smooth: true, symbol: "none", itemStyle: { color: METRIC_COLOR.adr }, lineStyle: { width: 1.5, color: METRIC_COLOR.adr }, data: buckets.map((b) => Math.round(b.adr)) },
        {
          name: cmpAdrName, type: "line", yAxisIndex: 1, smooth: true, symbol: "none",
          lineStyle: { type: "dashed", color: METRIC_COLOR.adr, width: 1.5, opacity: 0.7 }, itemStyle: { color: METRIC_COLOR.adr },
          data: buckets.map((b, i) => { const c = cmpFor(b, i); return c && c.use ? Math.round(c.adr) : null; }),
        },
      ],
    };
  };

  pieOption = (rows) => {
    const isRev = this.state.breakdownMetric === "rev";
    return {
      tooltip: { trigger: "item", formatter: (p) => `${p.name}<br/>${isRev ? "NT$" + Number(p.value).toLocaleString() : Number(p.value).toLocaleString() + " 夜"} (${p.percent}%)<br/>ADR NT$${Number(p.data.adr || 0).toLocaleString()}` },
      series: [
        {
          type: "pie", radius: ["36%", "60%"], center: ["50%", "50%"], avoidLabelOverlap: true, minShowLabelAngle: 4,
          itemStyle: { borderColor: "#fff", borderWidth: 2 },
          // 數據直接標在圖上 (名稱 + 實際數值 + 佔比 + ADR)，不用圖例
          label: {
            show: true, lineHeight: 13, color: "#595959",
            // 窄螢幕只留名稱與佔比：金額與 ADR 兩行放不下，會被容器裁成「NT$1,946,15…」，
            // truncated 的數字比沒有數字更糟。完整內容仍在 tooltip 裡
            formatter: (p) => (this.state.narrow
              ? `{n|${p.name}}\n{v|${p.percent}%}`
              : `{n|${p.name}}\n{v|${this.mFmt(p.value)} · ${p.percent}%}\n{a|ADR ${Number(p.data.adr || 0).toLocaleString()}}`),
            rich: { n: { fontSize: 11, fontWeight: 600, lineHeight: 14 }, v: { fontSize: 11 }, a: { fontSize: 10, color: MUTED_COLOR } },
          },
          labelLine: { show: true, length: 10, length2: 10 },
          data: rows.map((r, i) => ({ name: r.name, value: r.value, adr: r.adr, itemStyle: { color: CH_COLORS[i % CH_COLORS.length] } })),
        },
      ],
    };
  };

  otbOption = () => {
    const { buckets } = this.forecast();
    const isDaily = this.unitFor(this.state.gran) === "day"; // 日粒度前瞻才有 ly_rooms、可講「還剩幾天已賣幾間」
    // 逐日在手的團體/散客分層 (ops/pace)。有它時柱高、房晚與錢的三條線一律改吃它 ——
    // 正常帶是 ORDER_DT as-of 還原，本地 dayItems 是 RVUSE_DT 現況，兩者不是同一把尺
    // (2026/09/14 實測 19 vs 22 間)。只換柱不換 RevPAR，同一個 tooltip 裡就會出現
    // 「在手 19%（19 間）」配上用 22 間算出來的 RevPAR，差了一成六。
    const pcMap = this.paceByDate();
    // 一律先確認桶存在再取值：切換粒度時 echarts 會用「上一個資料集」的 dataIndex 回呼
    // label 與 tooltip 的 formatter (年 12 桶 → 月 90 桶)，直接 buckets[i].occ 會丟例外，
    // 而 formatter 丟出來的例外會一路往上炸掉整張儀表板 (ErrorBoundary 接住後畫面全空)。
    const keyFmt = this.paceKeyFmt(this.state.gran);
    const bucketAt = (i) => buckets[i] || null;
    const pcOf = (i) => { const b = bucketAt(i); return b ? (pcMap[b.m.format(keyFmt)] || null) : null; };
    const hasSplit = this.hasPaceSplit();
    // 沒有分層時退回本地資料，但一樣先四捨五入 —— 原本 tooltip 用的是已捨入的 barData，
    // 改讀 b.occ 會把 18.627450980392158% 直接印出來
    const occAt = (i) => { const b = bucketAt(i); if (!b) return 0; const pc = pcOf(i); return pc ? pc.otb_occ : Number(b.occ.toFixed(1)); };
    const roomsAt = (i) => { const b = bucketAt(i); if (!b) return 0; const pc = pcOf(i); return pc ? pc.otb_rooms : b.use; };
    const barData = buckets.map((_, i) => occAt(i));
    const adrData = buckets.map((b) => (b.adr ? Math.round(b.adr) : null)); // 前瞻各桶在手 ADR (純房租)
    const revparData = buckets.map((b, i) => (b.adr ? Math.round(b.adr * occAt(i) / 100) : null)); // 在手 RevPAR = ADR × 在手率
    const revData = buckets.map((b, i) => (b.adr ? Math.round(b.adr * roomsAt(i)) : null));        // 在手房租營收 = ADR × 售出房晚
    // hideOverlap：90 天前瞻的日標籤放不下，讓 echarts 自行疏開 (interval 0 只是「先全列出來」)
    const xAxis = { type: "category", data: buckets.map((b) => b.label), axisLabel: { fontSize: 10, interval: 0, hideOverlap: true, rotate: buckets.length > 7 ? 40 : 0, formatter: (v) => (isWeekendLabel(v) ? `{wk|${v}}` : v), rich: { wk: { color: WEEKEND_COLOR, fontWeight: "bold", fontSize: 10 } } } };
    const yAxisOcc = { type: "value", min: 0, max: 100, axisLabel: { formatter: "{value}%" } };
    const yAxisMoney = { type: "value", min: 0, position: "right", axisLabel: { fontSize: 9, color: METRIC_COLOR.revpar, formatter: kAxis }, splitLine: { show: false } };
    const yAxis = [yAxisOcc, yAxisMoney];
    // RevPAR(紫)為主錢線 (決策看它);ADR(綠)同量級、預設隱藏可點圖例開;營收金額見 tooltip
    const revparSeries = { name: "RevPAR", type: "line", smooth: true, symbol: "none", yAxisIndex: 1, connectNulls: true, data: revparData, z: 6, itemStyle: { color: METRIC_COLOR.revpar }, lineStyle: { color: METRIC_COLOR.revpar, width: 1.5 } };
    const adrSeries = { name: "ADR", type: "line", smooth: true, symbol: "none", yAxisIndex: 1, connectNulls: true, data: adrData, z: 5, itemStyle: { color: METRIC_COLOR.adr }, lineStyle: { color: METRIC_COLOR.adr, width: 1.5 } };

    // 有訂房曲線正常帶：P25–P75 帶 + 去年同進度線 (月前瞻=逐日 / 季=週 / 年=月)
    const pbMap = {};
    this.state.paceBand.forEach((p) => { pbMap[p.dat] = p; });
    const pb = buckets.map((b) => pbMap[b.m.format(keyFmt)] || null);
    if (pb.some((p) => p)) {
      const lo = pb.map((p) => (p ? p.lo : null));
      const band = pb.map((p) => (p ? Math.max(0, Number((p.hi - p.lo).toFixed(1))) : null));
      const ly = pb.map((p) => (p ? p.ly : null));
      const paceOf = (i) => this.paceStatus(pb[i], occAt(i));
      const cmp = this.forecastCmpBuckets(); // 去年最終實際 (同位置)
      const today0 = moment().startOf("day");
      // 堆疊柱：散客在下 (平日幾乎是一條平線)、團體在上 (逐日高低起伏多半來自它)。
      // 兩段各自換算成佔可售房數的百分比，與 y 軸、正常帶同一個尺度。
      const pct = (rooms, cap) => (cap ? Number(((rooms / cap) * 100).toFixed(1)) : 0);
      const fitData = buckets.map((_, i) => { const pc = pcOf(i); return pc ? pct(pc.otb_fit, pc.cap) : null; });
      // 團體為 0 的日子把圓角讓給散客那一段，否則整根柱會是平頂，看起來像被截斷
      const grpData = buckets.map((_, i) => {
        const pc = pcOf(i);
        if (!pc) return null;
        return { value: pct(pc.otb_group, pc.cap), itemStyle: { borderRadius: pc.otb_group > 0 ? [4, 4, 0, 0] : 0 } };
      });
      const fitStyled = buckets.map((_, i) => {
        const pc = pcOf(i);
        if (!pc) return null;
        return { value: fitData[i], itemStyle: { borderRadius: pc.otb_group > 0 ? 0 : [4, 4, 0, 0] } };
      });
      // 訂房進度的非顏色通道 —— 但只標「落後」。
      //
      // 實測 90 天窗口裡 63 天是超前、24 天正常、3 天落後：遠期的正常帶本來就貼近零，
      // 稍微有一點在手就算超前。在 63 根柱上標 ▲ 等於替常態做記號，把真正的例外埋掉；
      // 高柱的標籤還會頂出 100% 軸線被裁掉。落後才是要人動作的例外，標它才有訊息量。
      // 四種狀態的完整文字在表格檢視裡逐日都有，輔助科技與鍵盤走那條路。
      const paceMarkLabel = {
        show: true, position: "top", distance: 2,
        // label.color 不吃 callback，但這裡只剩單一顏色，直接給即可
        color: PACE_COLOR.落後, fontSize: 10,
        formatter: (p) => (paceOf(p.dataIndex) === "落後" ? PACE_MARK.落後 : ""),
      };
      return {
        tooltip: { trigger: "axis", formatter: (ps) => {
          const i = ps[0].dataIndex, p = pb[i], b = bucketAt(i);
          if (!b) return ""; // 同上：過期索引
          const out = [];
          if (isDaily) {
            const lead = b.m.clone().startOf("day").diff(today0, "days");
            const lyDate = b.m.clone().subtract(364, "days").format("M/D"); // 364 天 = 同星期,與 paceBand/forecastCmp 對齊
            out.push(`${b.label}　今年在手 ${occAt(i)}%（${roomsAt(i)} 間）`);
            const pc = pcOf(i);
            if (pc) out.push(`　└ 團體 ${pc.otb_group} 間／散客 ${pc.otb_fit} 間`);
            if (p && p.ly_rooms != null) out.push(`去年 ${lyDate} 在還剩 ${lead} 天時 → 已賣 ${p.ly_rooms} 間`);
            if (cmp[i] && cmp[i].cap) out.push(`去年 ${lyDate} 最後 → 賣到 ${cmp[i].use} 間`);
          } else {
            out.push(`${b.label}　今年在手 ${occAt(i)}%`);
            if (p && p.ly != null) out.push(`去年同時間點在手 ${p.ly}%`);
            if (cmp[i] && cmp[i].cap) out.push(`去年最後 ${cmp[i].occ.toFixed(0)}%`);
          }
          if (p && paceOf(i) === "尚早") out.push(`近 ${p.n} 年此時也幾乎還沒開賣 (正常帶 ${p.lo}–${p.hi}%) → 太早，還看不出超前/落後`);
          else if (p) {
            out.push(`近 ${p.n} 年正常帶 ${p.lo}–${p.hi}%`);
            // 364 天對齊的是星期，農曆節慶會在樣本之間漂移 —— 樣本裡混了假日，正常帶就會偏高，
            // 不講出來的話這一天會被當成「經營落後」，實際上只是拿平日去比連假
            const hol = isDaily ? holidaySamples(b.m.format("YYYY/MM/DD"), p.n, this.state.bandHolidays) : [];
            if (hol.length) out.push(`　⚠ ${holidaySampleText(hol, p.n)}`);
            out.push(`→ 今年${paceOf(i)}`);
          }
          if (adrData[i] != null) out.push(`ADR NT$${adrData[i].toLocaleString()}　RevPAR NT$${revparData[i].toLocaleString()}　營收 NT$${revData[i].toLocaleString()}`);
          return out.join("<br/>");
        } },
        // 不分層時「在手 (OTB)」不列進圖例：柱色是 callback (依超前/落後逐柱決定)，
        // echarts 的圖例拿不到那個函式的結果，只能退回調色盤預設色 —— 於是圖例上那個色塊
        // 在圖上一根柱都對不到。超前/正常/落後/尚早的色彩語彙由卡頭圖例負責。
        legend: { top: 0, right: 6, itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11 },
          data: (hasSplit ? ["散客", "團體"] : []).concat(["去年·同樣提前天數", "去年最終", "近年正常帶", "ADR", "RevPAR"]), selected: { ADR: false } },
        grid: { top: this.state.narrow ? 72 : 28, left: 40, right: 50, bottom: 44 },
        xAxis, yAxis,
        series: [
          { name: "正常帶下界", type: "line", stack: "band", symbol: "none", lineStyle: { opacity: 0 }, data: lo, silent: true, tooltip: { show: false }, z: 1 },
          { name: "近年正常帶", type: "line", stack: "band", symbol: "none", lineStyle: { opacity: 0 }, areaStyle: { color: "rgba(24,144,255,0.14)" }, data: band, silent: true, z: 1 },
          ...(hasSplit ? [
            { name: "散客", type: "bar", stack: "otb", data: fitStyled, z: 3, itemStyle: { color: SEGMENT_COLOR.fit } },
            { name: "團體", type: "bar", stack: "otb", data: grpData, z: 3, itemStyle: { color: SEGMENT_COLOR.group }, label: paceMarkLabel },
          ] : [
            { name: "在手 (OTB)", type: "bar", data: barData, z: 3, label: paceMarkLabel,
              itemStyle: { borderRadius: [4, 4, 0, 0], color: (pp) => PACE_COLOR[paceOf(pp.dataIndex)] || PACE_COLOR.正常 } },
          ]),
          { name: "去年·同樣提前天數", type: "line", smooth: true, symbol: "circle", symbolSize: 4, connectNulls: true, data: ly, z: 4, itemStyle: { color: METRIC_COLOR.occ }, lineStyle: { type: "dashed", color: METRIC_COLOR.occ, width: 1.5, opacity: 0.7 } },
          // 去年最終：那些日期去年最後做到多少 (需求天花板)。遠期 pace 近零時，看這條才知道還有多少進單空間。
          { name: "去年最終", type: "line", smooth: true, symbol: "none", connectNulls: true, data: buckets.map((_, i) => (cmp[i] && cmp[i].cap ? Number(cmp[i].occ.toFixed(1)) : null)), z: 2, itemStyle: { color: METRIC_COLOR.occ }, lineStyle: { type: "dotted", color: METRIC_COLOR.occ, width: 1.5, opacity: 0.6 } },
          adrSeries,
          revparSeries,
        ],
      };
    }

    // 無正常帶 (非本週 / 非日粒度)：維持 OTB 柱 + 去年最終實際線
    const cmp = this.forecastCmpBuckets();
    const lyData = buckets.map((_, i) => (cmp[i] && cmp[i].cap ? Number(cmp[i].occ.toFixed(1)) : null));
    const hasLy = lyData.some((v) => v != null);
    return {
      tooltip: { trigger: "axis", formatter: (ps) => {
        const i = ps[0].dataIndex;
        const lines = ps.filter((p) => p.data != null).map((p) => `${p.marker}${p.seriesName} ${(p.seriesName === "RevPAR" || p.seriesName === "ADR") ? "NT$" + Number(p.data).toLocaleString() : p.data + "%"}`);
        if (adrData[i] != null) lines.push(`營收 NT$${revData[i].toLocaleString()}`);
        return [ps[0].axisValue].concat(lines).join("<br/>");
      } },
      // 同上：柱色依平日/週末 callback 決定，列進圖例只會出現一個對不到任何柱的色塊
      legend: { top: 0, right: 6, itemWidth: 12, itemHeight: 8, textStyle: { fontSize: 11 }, data: hasLy ? ["去年同期實際", "ADR", "RevPAR"] : ["ADR", "RevPAR"], selected: { ADR: false } },
      grid: { top: this.state.narrow ? 72 : 28, left: 40, right: 50, bottom: 44 },
      xAxis, yAxis,
      series: [
        { name: "在手 (OTB)", type: "bar", data: barData, itemStyle: { borderRadius: [4, 4, 0, 0], color: (p) => (buckets[p.dataIndex].weekend ? "#faad14" : "#13c2c2") } },
        { name: "去年同期實際", type: "line", smooth: true, symbol: "circle", symbolSize: 5, connectNulls: true, data: lyData, itemStyle: { color: METRIC_COLOR.occ }, lineStyle: { type: "dashed", color: METRIC_COLOR.occ, width: 1.5, opacity: 0.7 } },
        adrSeries,
        revparSeries,
      ],
    };
  };

  /** 前瞻圖是否畫得出團體/散客分層 (ops/pace 已回來即可，各粒度皆適用)。圖例、柱色與表格必須用同一個判準。 */
  hasPaceSplit = () => (this.state.pace || []).length > 0;

  /** ops/pace 的逐日查表。圖 (otbOption) 與表 (forecastRows) 共用，兩邊不能各自建一份。 */
  paceByDate = () => {
    const m = {};
    (this.state.pace || []).forEach((p) => { m[p.dat] = p; });
    return m;
  };

  /**
   * 落後正常帶的彙總。
   *
   * 原本卡片上的「最低」是對 90 個桶取原始在手率最小值 —— 遠期本來就還沒開賣
   * (實測窗口末端只有 2 間、約 2%)，所以它必然指向三個月後的某一天，永遠指不到
   * 真正該處理的日子。真正的判準是「相對同提前天數的正常帶落後多少」，而那個
   * 結論 paceStatus() 早就算得出來，只是以前算完只拿去塗柱子顏色就丟掉了。
   *
   * 行動窗口沿用定價清單的 LOWER_MAX_LEAD：更早的低在手多半只是正常的提前節奏。
   */
  behindSummary = () => {
    const rows = this.forecastRows().filter((r) => r.status === "落後" && r.bandLo != null);
    const today0 = moment().startOf("day");
    const inWindow = rows.filter((r) => moment(r.dat, "YYYY/MM/DD").diff(today0, "days") <= LOWER_MAX_LEAD);
    const pool = inWindow.length ? inWindow : rows; // 行動窗口內沒有落後日時退回整個窗口，仍好過原始最小值
    const worst = pool.reduce((m, r) => (m == null || r.occ - r.bandLo < m.occ - m.bandLo ? r : m), null);
    return {
      count: rows.length,
      actionable: inWindow.length,
      rooms: rows.reduce((n, r) => n + Math.max(0, (r.cap || 0) - (r.rooms || 0)), 0),
      worst,
    };
  };

  /**
   * 前瞻逐桶的表格列。與 otbOption() 吃同一份 state，確保圖與表講同一件事 ——
   * 表格是 canvas 的等價入口 (螢幕閱讀器、鍵盤、定位到某一天)，數字對不上就失去意義。
   */
  forecastRows = () => {
    // 記憶化：一輪 render 裡 behindSummary、otbOption 的鄰居、表格檢視各會叫一次，
    // 每次都要重跑 bucketize 並對 90 列各算 5 個對照樣本日期 (450 個 moment 物件)。
    // state 的陣列都是整個換掉的，比 identity 就夠。
    const s = this.state;
    const key = [s.gran, s.offset, s.dayItems, s.monthItems, s.pace, s.paceBand, s.bandHolidays,
      moment().format("YYYY/MM/DD")];
    if (this._rowsMemo && this._rowsMemo.key.every((v, i) => v === key[i])) return this._rowsMemo.rows;
    const { buckets } = this.forecast();
    const keyFmt = this.paceKeyFmt(this.state.gran);
    const isDaily = this.unitFor(this.state.gran) === "day";
    const pbMap = {};
    this.state.paceBand.forEach((p) => { pbMap[p.dat] = p; });
    const pcMap = this.paceByDate();
    const rows = buckets.map((b) => {
      const pb = pbMap[b.m.format(keyFmt)] || null;
      const pc = pcMap[b.m.format(keyFmt)] || null;
      const occ = pc ? pc.otb_occ : Number(b.occ.toFixed(1));
      return {
        key: b.m.format("YYYY/MM/DD"), dat: b.m.format("YYYY/MM/DD"), label: b.label, isDaily,
        occ, rooms: pc ? pc.otb_rooms : b.use, cap: pc ? pc.cap : b.cap,
        group: pc ? pc.otb_group : null,
        fit: pc ? pc.otb_fit : null,
        bandLo: pb ? pb.lo : null, bandHi: pb ? pb.hi : null,
        bandYears: pb ? pb.n : null,
        // 週/月桶的對照樣本是整段區間的位移，不是單一天 —— 只有日粒度算得出正確的樣本日期
        bandHolidaySamples: (pb && isDaily) ? holidaySamples(b.m.format("YYYY/MM/DD"), pb.n, this.state.bandHolidays) : [],
        status: this.paceStatus(pb, occ),
        adr: b.adr ? Math.round(b.adr) : null,
      };
    });
    this._rowsMemo = { key, rows };
    return rows;
  };

  // 各前瞻桶的近期淨 pickup (新增−取消)，與 forecast 同 x 軸；供 velocityOption (訂房增速圖) 使用。
  bucketPickup = (buckets = this.forecast().buckets) => {
    const pickup = this.state.pickup || [];
    const bUnit = this.unitFor(this.state.gran);
    return buckets.map((b) => {
      const start = b.m.clone().startOf(bUnit), end = b.m.clone().endOf(bUnit);
      let added = 0, cancelled = 0;
      pickup.forEach((p) => {
        const d = moment(p.dat, "YYYY/MM/DD");
        if (d.isBetween(start, end, "day", "[]")) { added += Number(p.added) || 0; cancelled += Number(p.cancelled) || 0; }
      });
      return { b, label: b.label, added, cancelled, net: added - cancelled };
    });
  };

  // 依訂房日圖的分桶：把近期訂房日 (state.velocity) 依檢視粒度聚合 (週=逐日 / 月=逐週 / 季年=逐月)，
  // 與依入住日同粒度、同標籤 (bucketLabel)，解決兩圖粒度不一致
  bookingBuckets = () => {
    const unit = this.unitFor(this.state.gran);
    const fmt = this.bucketKeyFmt(unit);
    const map = new Map();
    (this.state.velocity || []).forEach((v) => {
      const m = moment(v.dat, "YYYY/MM/DD");
      const key = m.clone().startOf(unit).format(fmt);
      let b = map.get(key);
      if (!b) { b = { m: m.clone().startOf(unit), added: 0, cancelled: 0 }; map.set(key, b); }
      b.added += Number(v.added) || 0; b.cancelled += Number(v.cancelled) || 0;
    });
    return [...map.values()].sort((a, b) => a.m.valueOf() - b.m.valueOf())
      .map((b) => ({ m: b.m, label: this.bucketLabel(unit, b.m), added: b.added, cancelled: b.cancelled, net: b.added - b.cancelled }));
  };

  /**
   * 兩張增速圖各自的資料窗口 —— 必須與 fetch 時傳給後端的參數一致，
   * 否則抽屜會查到柱從來沒算過的訂單。天數兩張圖不同：
   * 依訂房日用 velDays() (隨粒度 30/84/120)，依入住日固定 30 (見 fetchPace 的 pacePickup 呼叫)。
   */
  velocityWindows = (mode) => {
    const fr = this.forecastRange();
    const days = mode === "book" ? this.velDays() : 30;
    const today = moment().startOf("day");
    return {
      stayWindow: { from: fr.start.clone().startOf("day"), to: fr.to.clone().startOf("day") },
      bookWindow: { from: today.clone().subtract(days - 1, "days"), to: today },
    };
  };

  // 點訂房增速的柱 → 開抽屜看這根柱底下的實際訂單 (哪些單灌進來、哪些被退掉)
  // 收的是分類索引而非 echarts 事件 —— 命中判定由 ResponsiveECharts 的 onCategoryClick
  // 以座標換算完成，整個繪圖區都可點 (柱子本身在窄螢幕只有幾個像素寬)
  onVelocityBarClick = (mode, dataIndex) => {
    const agg = mode === "book" ? this.bookingBuckets() : this.bucketPickup();
    const bucket = agg[dataIndex];
    if (!bucket) return;
    const unit = this.unitFor(this.state.gran);
    const range = pickupOrdersRange({
      mode, unit,
      bucketStart: mode === "book" ? bucket.m : bucket.b.m,
      ...this.velocityWindows(mode),
    });
    if (!range) return;

    const title = `訂房增速明細 · ${mode === "book" ? "依訂房日" : "依入住日"} ${bucket.label}`;
    this.loadOrders(title, PmsAPI.ops.pickupOrders(range.stayFrom, range.stayTo, range.bookFrom, range.bookTo));
  };

  // 訂房曲線抽屜 → 該住用區間目前在手的訂單。
  // 與增速圖的鑽取是不同口徑 (在手 vs 進出)，但共用同一個抽屜元件，由回應的 basis 決定表頭與欄位
  openOtbOrders = () => {
    const { curveFrom, curveTo, curveStay, curveRoom } = this.state;
    if (!curveFrom || !curveTo) return;
    // 帶上房型：曲線正看著某個房型時，清單卻列全館會是兩個畫面在講不同的事
    const room = curveRoom ? this.roomNameOf(curveRoom) : null;
    this.loadOrders(`目前在手訂單 · ${curveStay || ""}${room ? ` · ${room}` : ""}`,
      PmsAPI.ops.otbOrders(curveFrom, curveTo, curveRoom));
  };

  /** 房型代碼 → 中文名，取自 breakdown 已載入的房型維度。查不到就回代碼本身。 */
  roomNameOf = (cod) => {
    const r = ((this.state.breakdown && this.state.breakdown.roomType) || [])
      .find((x) => String(x.room_cod).trim() === cod);
    return r ? (r.room_nam || r.room_cod) : cod;
  };

  /** 兩個入口共用的載入流程：開抽屜、清掉上一次的資料與錯誤、接回應。 */
  loadOrders = (title, request) => {
    this.setState({ ordersOpen: true, ordersTitle: title, ordersData: null, ordersError: null, ordersLoading: true });
    request
      .then((r) => this.setState({ ordersData: r.data }))
      .catch((e) => this.setState({ ordersError: (e && e.message) || "載入失敗" }))
      .finally(() => this.setState({ ordersLoading: false }));
  };

  // 訂房增速迷你圖：兩種鏡頭 (上下並列同時看) —— stay=依入住日 (各前瞻桶近 30 日淨 pickup) / book=依訂房日 (近期依粒度聚合的每桶新增)，柱綠正/紅負
  velocityOption = (mode = "stay") => {
    const book = mode === "book";
    const agg = book ? this.bookingBuckets() : this.bucketPickup();
    const net = agg.map((a) => a.net);
    const many = agg.length > 7;
    const bookUnitWord = { day: "日", isoWeek: "週", month: "月" }[this.unitFor(this.state.gran)] || "日";
    const addLbl = book ? `每${bookUnitWord}新增` : "近 30 日新增", netLbl = book ? "淨新增" : "淨進單";
    return {
      tooltip: { trigger: "axis", formatter: (ps) => { const a = agg[ps[0].dataIndex]; return `${a.label}<br/>${addLbl} ${a.added} 間夜<br/>取消 ${a.cancelled} 間夜<br/><b>${netLbl} ${a.net} 間夜</b>`; } },
      // containLabel：軸標籤自動納入邊界。固定 left:28 只夠放 3 位數 —— 年/季粒度聚合後
      // 淨房晚會破千，echarts 以千分位格式化 ('1,200' 寬 22.5px > 可用的 20px)，數字會被畫布左緣切掉。
      grid: { top: 8, left: 6, right: 8, bottom: 4, containLabel: true },
      xAxis: { type: "category", data: agg.map((a) => a.label), axisTick: { show: false }, axisLabel: { fontSize: 10, interval: 0, rotate: many ? 40 : 0, hideOverlap: true, formatter: (val) => (isWeekendLabel(val) ? `{wk|${val}}` : val), rich: { wk: { color: WEEKEND_COLOR, fontWeight: "bold", fontSize: 10 } } } },
      yAxis: { type: "value", axisLabel: { fontSize: 9 }, splitLine: { lineStyle: { color: "#f5f5f5" } } },
      series: [
        { name: netLbl, type: "bar", barMaxWidth: 14, data: net.map((n) => ({ value: n, itemStyle: { color: n >= 0 ? "#52c41a" : "#cf1322" } })),
          // label 關掉：markLine 預設會在線的右端印出值 ("0")，那串字被畫布右緣切掉一半，
          // 而且 y 軸本來就標了 0，等於多一個看不懂的殘字。
          markLine: { silent: true, symbol: "none", label: { show: false }, data: [{ yAxis: 0, lineStyle: { color: MARK_MUTED_COLOR, width: 1 } }] } },
      ],
    };
  };

  /**
   * 一張增速圖的三種狀態。兩張圖資料來源不同 (stay=pickup / book=velocity)，各自判斷：
   * 載入中 → Skeleton；載入完但沒資料 → 講清楚是沒資料，不要用空的座標軸充數
   * (bookingBuckets 在 velocity 為空時回傳 []，畫出來是一個沒有任何軸標籤的空框)；有資料才畫圖。
   * 與本檔其他圖表一致 (見趨勢圖 tb.length、前瞻圖 fc.buckets.length)。
   */
  velocityChart = (mode, loading, hasData, onClick) => {
    if (loading) return <ResponsiveECharts option={null} style={{ height: 150 }} />;
    if (!hasData) return <div style={{ height: 150, display: "flex", alignItems: "center", justifyContent: "center", color: FAINT_COLOR, fontSize: FONT.caption }}>此期間查無訂房進出紀錄</div>;
    return <ResponsiveECharts option={this.velocityOption(mode)} style={{ height: 150 }} onCategoryClick={onClick}
      ariaLabel={mode === "stay"
        ? "訂房增速 (依入住日)：前瞻窗口內每個未來日、近 30 天成立與取消的淨訂房房晚，綠柱為淨新增、紅柱為淨取消。"
        : "訂房增速 (依訂房日)：近期每個訂房日對未來期的淨新增房晚，綠柱為淨新增、紅柱為淨取消。"} />;
  };

  // 訂房增速摘要：近 7 日 vs 前 7 日淨訂房，判定情境 (hot=加速 / cold=停滯)。供標題徽章使用。
  velocitySummary = () => {
    const v = this.state.velocity || [];
    if (v.length < 14) return null;
    const sum = (arr) => arr.reduce((s, x) => s + (Number(x.net) || 0), 0);
    const last7 = sum(v.slice(-7));
    const prev7 = sum(v.slice(-14, -7));
    const accelPct = prev7 > 0 ? Math.round((last7 / prev7 - 1) * 100) : null;
    let scen = null; // 'hot' 連續加速 / 'cold' 趨零或轉負；用四捨五入後的 accelPct，與標題徽章一致
    if (last7 <= 0) scen = "cold";
    else if (accelPct != null && accelPct >= 25 && last7 >= 20) scen = "hot";
    return { last7, prev7, accelPct, scen };
  };

  // 開單一日期的訂房曲線抽屜。前瞻圖點柱 (日粒度) 與定價清單點日期共用同一條路徑。
  openCurveForDate = (dat) => {
    const m = moment(dat, "YYYY/MM/DD");
    const f = (x) => x.format("YYYY/MM/DD");
    // 一併記下區間：抽屜裡的「目前在手訂單」要照同一段查，只留標籤字串就得再從它反推。
    // 每次重開都把房型選擇歸零 —— 沿用上一個日期選的房型會讓人以為看的是全館
    this.setState({ curveOpen: true, curveStay: m.format("M/D (dd)"), curveFrom: f(m), curveTo: f(m), curveRoom: null },
      this.loadCurve);
  };

  /** 依目前的 curveFrom/curveTo/curveRoom 載入曲線。切換房型時重跑同一條路徑。 */
  loadCurve = () => {
    const { curveFrom, curveTo, curveRoom } = this.state;
    if (!curveFrom || !curveTo) return;
    this.setState({ curveData: null, curveLoading: true });
    PmsAPI.ops.paceCurve(curveFrom, curveTo, moment().format("YYYY/MM/DD"), 5, curveRoom)
      .then((r) => this.setState({ curveData: r.data.items || [], curveCurLead: r.data.cur_lead }))
      .catch(() => this.setState({ curveData: [] }))
      .finally(() => this.setState({ curveLoading: false }));
  };

  setCurveRoom = (roomCod) => this.setState({ curveRoom: roomCod || null }, this.loadCurve);

  /**
   * 房型維度點「近期進單」→ 該房型本期的訂房曲線。
   *
   * 區間用檢視期間 (this.range) 而非未來 90 天：那一欄講的就是「本期」的淨進單，
   * 點開來若換成別的區間，看到的曲線與剛才那個數字無關。
   */
  openCurveForRoom = (roomCod) => {
    const { from, to } = this.range(this.state.gran);
    const f = (m) => m.format("YYYY/MM/DD");
    this.setState({
      curveOpen: true,
      curveStay: `${from.format("YYYY/MM/DD")}–${to.format("MM/DD")}`,
      curveFrom: f(from), curveTo: f(to), curveRoom: roomCod,
    }, this.loadCurve);
  };

  // 點前瞻柱 → 開抽屜看該桶 (日/週/月) 的完整訂房曲線；lead 相對桶起始日，OTB 為期間彙總在手率
  // 穩定的參考：直接寫 this.onVelocityBarClick("stay") 會在每次 render 產生新函式，
  // 讓 ResponsiveECharts 的 effect 反覆解綁重綁 zr 監聽
  onVelocityStayClick = (i) => this.onVelocityBarClick("stay", i);
  onVelocityBookClick = (i) => this.onVelocityBarClick("book", i);

  /**
   * 曲線抽屜的前一日/後一日。
   *
   * 只對單日曲線開放 (curveFrom === curveTo)：週/月桶往前後推一整段，使用者看到的
   * 區間標題與 lead 基準都會跟著跳，那不是「看旁邊一天」而是換了個問題。
   * 房型維度開的曲線 (curveRoom 不為 null) 一樣可以走，房型跟著帶過去。
   */
  stepCurveDay = (delta) => {
    const { curveFrom, curveTo } = this.state;
    if (!curveFrom || curveFrom !== curveTo) return;
    const m = moment(curveFrom, "YYYY/MM/DD").add(delta, "day");
    const f = (x) => x.format("YYYY/MM/DD");
    this.setState({ curveStay: m.format("M/D (dd)"), curveFrom: f(m), curveTo: f(m) }, this.loadCurve);
  };

  /** 依桶起始日鑽取，語意與點柱一致 —— 表格列與統計格都走這裡，三個入口不能開出不同區間。 */
  openCurveForBucketDate = (dat) => {
    const idx = this.forecast().buckets.findIndex((b) => b.m.format("YYYY/MM/DD") === dat);
    if (idx >= 0) this.onForecastBarClick(idx);
  };

  onForecastBarClick = (dataIndex) => {
    const b = this.forecast().buckets[dataIndex];
    if (!b) return;
    const unit = this.unitFor(this.state.gran); // 桶單位決定抽屜要看的區間 (日/週/月)
    if (unit === "day") { this.openCurveForDate(b.m.format("YYYY/MM/DD")); return; } // 單日桶：與定價清單共用 openCurveForDate
    let from, to, label;
    if (unit === "isoWeek") { from = b.m.clone().startOf("isoWeek"); to = from.clone().add(6, "days"); label = `${from.format("M/D")}–${to.format("M/D")} 這週`; }
    else { from = b.m.clone().startOf("month"); to = b.m.clone().endOf("month"); label = `${from.format("YYYY/MM")} 這月`; }
    const f = (m) => m.format("YYYY/MM/DD");
    this.setState({ curveOpen: true, curveStay: label, curveFrom: f(from), curveTo: f(to), curveRoom: null },
      this.loadCurve);
  };

  // ---- render helpers ----
  metricSeries = (key) => this.curRows(this.state.gran).map((it) => it[key]);

  fmtKpi = (kind, v) => (kind === "pct" ? `${Number(v).toFixed(1)}%` : `NT$${Math.round(v).toLocaleString()}`);

  // KPI 卡 key → 已實現/在手 (kpiCur.realized / kpiCur.otb) 對應欄位；卡的 sk 是給 metricSeries 用的日資料欄位，
  // 與 realized/otb 欄位命名不同 (且 trevpar/revenue 卡的 sk 皆為 total_revenue、netrevpar 卡的 sk 與 revpar 卡重複)，
  // 故已實現/在手小行改用卡的唯一 key 對映；netrevpar 無 realized/otb 對應值，不列入 (小行不顯示)。
  kpiSubMetric = { occupancy: "occ", adr: "adr", revpar: "revpar", trevpar: "trevpar", revenue: "revenue" };

  kpiSub = (key, part) => {
    const f = this.kpiSubMetric[key];
    if (!part || !f) return "—";
    return f === "occ" ? `${Number(part.occ).toFixed(1)}%` : `NT$${Number(part[f]).toLocaleString()}`;
  };

  // Net RevPAR：把權威 RevPAR 乘上 breakdown.net.net_ratio (扣掉以毛額記帳通路的佣金)，
  // 確保永遠 ≤ RevPAR 且與 KPI 口徑一致；對比期同法取 breakdownCmp。
  netRevpar = (revpar, bd) => {
    const n = bd && bd.net;
    return n && n.net_ratio != null && revpar != null ? Math.round(revpar * n.net_ratio) : null;
  };

  kpiCards = () => {
    const c = this.state.kpiCur, pf = this.state.kpiCmpFull; // 全期 vs 去年同期「最終值」(非同進度)
    if (!c) return [];
    const dFull = (a, b) => (pf ? this.delta(a, b) : null); // 全期軌 delta；未來期亦顯示 (搭配標題「（在手預估）」註記)
    const nrp = this.netRevpar(c.revpar, this.state.breakdown);
    const nrpP = this.netRevpar(pf && pf.revpar, this.state.breakdownCmp);
    return [
      { key: "occupancy", sk: "occupancy_rate", kind: "pct", title: "住房率 (OCC)", value: c.occ, suffix: "%", precision: 1, color: "#1890ff", d: dFull(c.occ, pf && pf.occ), pv: pf ? pf.occ : null },
      { key: "adr", sk: "adr", kind: "money", title: "平均房價 (ADR)", value: c.adr, prefix: "NT$", color: "#52c41a", d: dFull(c.adr, pf && pf.adr), pv: pf ? pf.adr : null },
      { key: "revpar", sk: "revpar", kind: "money", title: "每房營收 (RevPAR)", value: c.revpar, prefix: "NT$", color: "#722ed1", d: dFull(c.revpar, pf && pf.revpar), pv: pf ? pf.revpar : null },
      ...(nrp != null ? [{ key: "netrevpar", sk: "revpar", kind: "money", title: "每房淨營收 (Net RevPAR)", value: nrp, prefix: "NT$", color: "#08979c", d: nrpP != null ? this.delta(nrp, nrpP) : null, pv: nrpP }] : []),
      { key: "trevpar", sk: "total_revenue", kind: "money", title: "每房總營收 (TRevPAR)", value: c.trevpar, prefix: "NT$", color: "#eb2f96", d: dFull(c.trevpar, pf && pf.trevpar), pv: pf ? pf.trevpar : null },
      { key: "revenue", sk: "total_revenue", kind: "money", title: "期間營收 (Revenue)", value: c.revenue, prefix: "NT$", color: "#fa8c16", d: dFull(c.revenue, pf && pf.revenue), pv: pf ? pf.revenue : null },
    ];
  };

  // 堆疊條：已實現 (實色) + 在手 (同色淺透明) 兩段，佔比以 max 為基準；max<=0 時兩段皆 0 寬，不產生 NaN。
  splitBar = (rVal, oVal, max, color) => {
    const pR = max ? (rVal / max) * 100 : 0, pO = max ? (oVal / max) * 100 : 0;
    return (
      <div style={{ display: "flex", height: 8, background: "#f0f0f0", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${pR}%`, background: color }} />
        <div style={{ width: `${pO}%`, background: color, opacity: 0.4 }} />
      </div>
    );
  };

  // 各維度 (訂房來源/通路類型/銷售方案) 一列：標籤 + 數字 (全期，附「在手」小字) + splitBar。
  // row 需帶 r_nights/o_nights/r_rev/o_rev (topN()/channelTypeAgg() 已補上)；bar 與註記跟隨 breakdownMetric 切換 (營收/房晚)，
  // 與該列顯示的 value/mFmt 同一度量，避免營收檢視下條/註記卻是房晚比例的錯位。max 需為呼叫端算好的「同度量」最大值。
  splitBarRow = (label, row, max, color, extra) => {
    const isRev = this.state.breakdownMetric === "rev";
    const rVal = isRev ? Number(row.r_rev) || 0 : Number(row.r_nights) || 0;
    const oVal = isRev ? Number(row.o_rev) || 0 : Number(row.o_nights) || 0;
    return (
      <div key={label} style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span>{label}</span>
          <span style={{ fontWeight: 600 }}>
            {extra}
            {oVal > 0 && <span style={{ fontWeight: 400, color: "#d46b08", fontSize: FONT.caption, marginLeft: 6 }}>(在手 {this.mFmt(oVal)})</span>}
          </span>
        </div>
        {this.splitBar(rVal, oVal, max, color)}
      </div>
    );
  };

  // 目標達成卡的統計格：標籤 (小灰) 上、數值 (粗) 下、可選副標；不換行以維持對齊
  tgtStat = (label, value, color, sub) => (
    <div key={label} style={{ display: "flex", flexDirection: "column", lineHeight: 1.35 }}>
      <span style={{ color: MUTED_COLOR, whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ fontSize: FONT.title, fontWeight: 600, color: color || "#262626", whiteSpace: "nowrap" }}>{value}</span>
      {sub ? <span style={{ fontSize: FONT.caption, color: FAINT_COLOR, whiteSpace: "nowrap" }}>{sub}</span> : null}
    </div>
  );

  render() {
    const { gran, offset, breakdown, breakdownLoading, kpiCur, kpiCmp, kpiCmpFull } = this.state;
    const items = this.curRows(gran);
    // 明細對照桶的「已實現/在手/部分」標記：以桶與今天的關係判定，
    // 讓季/年檢視的未來桶不再被跳過，而是標「在手」；跨今天的當桶標「部分」。
    const today0 = moment().startOf("day");
    const phaseOf = (m, unit) => {
      const bLo = m.clone().startOf(unit), bHi = m.clone().endOf(unit);
      return bHi.isBefore(today0) ? "realized" : (bLo.isSameOrAfter(today0) ? "otb" : "mixed");
    };
    const tb = this.trendBuckets(gran);
    // 明細對照直接用走勢圖同一份桶 (月=日 / 季=週 / 年=月) 與同一套配對規則，表與圖的數字必然一致
    const tbCmp = this.trendCmpBuckets(gran);
    const bUnit = this.unitFor(gran);
    const cmpAt = this.cmpMatcher(gran, tbCmp);
    // 週列標出 ISO 週號 —— 對比就是比「第幾週」，標出來才看得懂為何配到那一列
    const rowLabel = (m) => (bUnit === "day" ? m.format("YYYY/MM/DD")
      : bUnit === "isoWeek" ? `W${m.isoWeek()} ${m.format("M/D")}–${m.clone().add(6, "days").format("M/D")}`
      : m.format("YYYY/MM"));
    const cmpRows = tb.map((b, i) => {
      const p = cmpAt(b, i);
      return {
        key: i,
        cDat: rowLabel(b.m), pDat: p && p.cap ? rowLabel(p.m) : null,
        cOcc: b.cap ? b.occ : null, pOcc: p && p.cap ? p.occ : null,
        cAdr: b.use ? b.adr : null, pAdr: p && p.use ? p.adr : null, // 純房租 ADR (與走勢圖同口徑)
        cRev: b.cap ? b.rev : null, pRev: p && p.cap ? p.rev : null,
        phase: phaseOf(b.m, bUnit),
      };
    });
    // 明細小計：住房率與 ADR 必須用加權 (總房晚÷總容量、總房租÷總房晚)，把各列的百分比再平均是錯的 ——
    // 每列的容量不同 (月底容量少的日子會被當成和滿月一樣重)。對比側只加總「有配對到」的桶，
    // 否則本期沒有的桶會墊高基準，小計的差額就對不上逐列的差額。
    const cmpSum = tb.reduce((a, b) => {
      const p = cmpAt(b);
      a.cUse += b.use; a.cCap += b.cap; a.cRent += b.rent; a.cRev += b.rev;
      if (p) { a.pUse += p.use; a.pCap += p.cap; a.pRent += p.rent; a.pRev += p.rev; }
      return a;
    }, { cUse: 0, cCap: 0, cRent: 0, cRev: 0, pUse: 0, pCap: 0, pRent: 0, pRev: 0 });
    const cmpTotal = {
      cOcc: cmpSum.cCap ? (cmpSum.cUse / cmpSum.cCap) * 100 : null,
      pOcc: cmpSum.pCap ? (cmpSum.pUse / cmpSum.pCap) * 100 : null,
      cAdr: cmpSum.cUse ? cmpSum.cRent / cmpSum.cUse : null,
      pAdr: cmpSum.pUse ? cmpSum.pRent / cmpSum.pUse : null,
      cRev: cmpSum.cCap ? cmpSum.cRev : null,
      pRev: cmpSum.pCap ? cmpSum.pRev : null,
      buckets: tb.length,
      matched: tb.filter((b) => cmpAt(b)).length,
    };
    const { from, to } = this.range(gran);
    const cur = this.agg(items);
    const { max, min } = this.extremes(tb.filter((b) => b.cap > 0));
    // 目標達成 (RevPAR)：目標 = 去年同期「全期最終」RevPAR ×(1+g)。cf = kpiCmpFull (全期，非同進度)；
    // 未來期 (offset>0) 亦可算 (kpiCmpFull 恆為去年已結束的全期值，不受本期未來影響)。
    const g = (this.state.growth || 0) / 100;
    const cf = kpiCmpFull;
    const tgtRevpar = cf && cf.revpar ? Math.round(cf.revpar * (1 + g)) : null; // RevPAR 目標 (併於目標達成卡標示)
    const attain = tgtRevpar && kpiCur ? (kpiCur.revpar / tgtRevpar) * 100 : null; // RevPAR 全期(含在手) 達標率
    // 期間營收總額目標 (主目標)：目標 = 去年同期「全期最終」營收 ×(1+g)
    const tgtRev = cf && cf.revenue ? Math.round(cf.revenue * (1 + g)) : null;
    const attainRev = tgtRev && kpiCur ? (kpiCur.revenue / tgtRev) * 100 : null;
    const attainRevReal = tgtRev && kpiCur && kpiCur.realized ? (kpiCur.realized.revenue / tgtRev) * 100 : null;
    const revYoY = kpiCur && cf ? Math.round(kpiCur.revenue - cf.revenue) : null;
    const revYoYPct = cf && cf.revenue ? Math.round((kpiCur.revenue / cf.revenue - 1) * 100) : null;
    const attainRevColor = attainRev == null ? MUTED_COLOR : attainRev >= 100 ? "#3f8600" : attainRev >= 90 ? "#faad14" : "#cf1322";
    const isDayRow = this.unitFor(gran) === "day"; // 逐日列才加星期標示；週/月列已是自帶區間的標籤
    const detailUnitWord = { day: "天", isoWeek: "週", month: "個月" }[this.unitFor(gran)]; // 小計列標示涵蓋幾桶
    const cmpCol = this.cmpYearWord(); // 明細對照的對比欄標題 (選定基準年)

    const source = this.sourceTop();
    const plan = this.planTop();
    // 各維度 splitBar 的縮放基準：同批列中「同度量」(row.value，已依 breakdownMetric 選營收或房晚) 的最大值，
    // 確保 bar 兩段與 max 同一單位，不會出現營收檢視卻用房晚比例的錯位；至少 1，避免除以 0。
    const srcMax = Math.max(1, ...source.rows.map((r) => Number(r.value) || 0));
    const planMax = Math.max(1, ...plan.rows.map((r) => Number(r.value) || 0));
    const ctRows = this.channelTypeAgg().rows;
    const ctMax = Math.max(1, ...ctRows.map((r) => Number(r.value) || 0));
    const fc = this.forecast();
    const fcAvg = fc.buckets.length ? fc.buckets.reduce((s, b) => s + b.occ, 0) / fc.buckets.length : 0;
    const fcPeak = fc.buckets.reduce((m, b) => (b.occ > m.occ ? b : m), fc.buckets[0] || { occ: 0, label: "—" });
    const behind = this.behindSummary();
    // 前瞻平均 ADR (OTB)：以房晚加權 (adr×use=rent)，與 KPI 純房租 ADR 同口徑
    const fcUse = fc.buckets.reduce((s, b) => s + (b.use || 0), 0);
    const fcAvgAdr = fcUse ? Math.round(fc.buckets.reduce((s, b) => s + (b.adr || 0) * (b.use || 0), 0) / fcUse) : 0;
    const vs = this.velocitySummary();
    const velTrend = vs ? (
      <span style={{ fontSize: FONT.caption, marginLeft: 8, color: vs.last7 <= 0 ? "#cf1322" : "#3f8600" }}>
        近 7 日淨 {vs.last7 >= 0 ? "+" : ""}{vs.last7} 間夜{vs.accelPct != null ? `・較前 7 日 ${vs.accelPct >= 0 ? "+" : ""}${vs.accelPct}%` : ""}
      </span>
    ) : null;
    const roomType = (breakdown && breakdown.roomType) || [];
    const rtTotalNights = roomType.reduce((s, r) => s + r.nights, 0) || 1;
    // roomType 的 r_nights/o_nights 已由後端 intval() 轉為整數，仍以 Number(x)||0 保底防呆。
    const rtMaxNights = Math.max(1, ...roomType.map((r) => Number(r.nights) || 0));
    // 期間天數 (供房型「待售間夜 / 營收空間」估算)：待售 = 實體房數×天數 − 在手間夜；營收空間 = 待售 × ADR
    const rtRange = this.range(gran);
    const rtDays = Math.max(1, rtRange.to.diff(rtRange.from, "days") + 1);
    const rtUnsold = (r) => Math.max(0, (Number(r.phys_rooms) || 0) * rtDays - (Number(r.nights) || 0));
    const rtUpside = (r) => rtUnsold(r) * (Number(r.adr) || 0);
    // 滿房天數排序比「比例」而不是天數：庫存表涵蓋 2018/11–2027/12，區間跨出範圍時
    // 各房型的 inv_days 會不同，直接比天數會把「資料較少」排成「較不滿房」
    const rtFullRatio = (r) => (r.full_days == null || !r.inv_days ? -1 : r.full_days / r.inv_days);
    // 各房型近 30 日淨 pickup (售罄動能)：net>0 仍在進單、<=0 停滯/淨退訂
    const rtPickMap = {};
    (this.state.roomPickup || []).forEach((p) => { rtPickMap[String(p.room_cod).trim()] = p; });
    const rtPick = (r) => rtPickMap[String(r.room_cod).trim()] || null;
    const rtCmpMap = {};
    ((this.state.breakdownCmp && this.state.breakdownCmp.roomType) || []).forEach((r) => { rtCmpMap[String(r.room_cod).trim()] = r; });
    // 部門營收改讀 kpiCur 的 rent/rs/other (與 breakdown 同區間同口徑，後端不再重複彙總)；
    // 客房/餐飲・服務另帶 r (已實現)/o (在手) 供 splitBar 拆分；其他未拆 (kpiCur.realized/otb 無 other 欄位)，維持全期單值。
    const kr = (kpiCur && kpiCur.realized) || {};
    const ko = (kpiCur && kpiCur.otb) || {};
    const deptRows = kpiCur
      ? [
          { label: "客房", v: Number(kpiCur.rent) || 0, r: Number(kr.rent) || 0, o: Number(ko.rent) || 0, color: "#1890ff" },
          { label: "餐飲・服務", v: Number(kpiCur.rs) || 0, r: Number(kr.rs) || 0, o: Number(ko.rs) || 0, color: "#13c2c2" },
          { label: "其他", v: Number(kpiCur.other) || 0, color: MARK_MUTED_COLOR }, // other 未拆，維持全期
        ]
      : [];
    const deptMax = deptRows.reduce((m, r) => Math.max(m, r.v), 0);
    const cn = breakdown && breakdown.cancelNoshow;
    const sold = cn ? Number(cn.sold) || 0 : 0;
    const cancelled = cn ? Number(cn.cancelled) || 0 : 0;
    const cancelRate = sold + cancelled ? (cancelled / (sold + cancelled)) * 100 : 0;
    // No-show 率：後端以「預期到店 − 實際到店 (TODAYCI_QNT)」推導 (NOSHOW_QNT 未啟用)。
    // 僅過去到店日可算；期間全為未來時 arrived_expected=0 → noshow_pct=null → 顯示「—」。
    const noshowPct = cn && cn.noshow_pct != null ? Number(cn.noshow_pct) : null;

    return (
      <Spin spinning={this.state.loading}>
        {/* 標題列 + 粒度切換 */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Typography.Title level={3} style={{ margin: 0 }}>
              營運儀表板 <span style={{ fontSize: FONT.body, color: MUTED_COLOR, fontWeight: 400 }}>
                {this.periodTitle()}{offset === 0 ? { week: "至今", month: "至今 (MTD)", quarter: "至今 (QTD)", year: "至今 (YTD)" }[gran] : offset > 0 ? "（在手預估）" : "（完整期間）"}
              </span>
            </Typography.Title>
            <Typography.Text type="secondary">
              結算期間：{this.dayWithWd(from, "YYYY/MM/DD")} – {this.dayWithWd(to, "MM/DD")}　·　對比 {this.prevLabel()}(全期) {this.prevRangeLabel(gran)}
            </Typography.Text>
            {kpiCur && (
              <div style={{ fontSize: FONT.caption, color: FAINT_COLOR }}>
                ADR/RevPAR 為純房租，TRevPAR 為含餐口徑
                {kpiCmp ? `　·　已實現對比 ${this.prevLabel()}同進度 ${kpiCmp.from}–${String(kpiCmp.to).slice(5)}` : ""}
              </div>
            )}
          </Col>
          <Col>
            <Space>
              {offset !== 0 && <Button size="small" onClick={() => this.setState({ offset: 0 }, this.fetchBreakdown)}>回本期</Button>}
              <Button.Group>
                <Button icon={<LeftOutlined aria-hidden />} onClick={() => this.shift(-1)} title="上一期" aria-label="上一期" />
                <Button icon={<RightOutlined aria-hidden />} onClick={() => this.shift(1)} title="下一期" aria-label="下一期" />
              </Button.Group>
              <Radio.Group value={gran} onChange={this.changeGran} optionType="button" buttonStyle="solid" aria-label="檢視粒度">
                <Radio.Button value="month">月</Radio.Button>
                <Radio.Button value="quarter">季</Radio.Button>
                <Radio.Button value="year">年</Radio.Button>
              </Radio.Group>
              {(
                <Space size={4}>
                  <span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>比較</span>
                  <Select size="small" value={this.state.cmpYears} onChange={this.setCmpYears} style={{ width: 120 }} aria-label="比較基準年份"
                    options={[1, 2, 3, 4, 5].map((n) => ({
                      value: n,
                      label: `${({ 1: "去年", 2: "前年", 3: "3 年前", 4: "4 年前", 5: "5 年前" }[n])} (${moment().add(offset, this.stepOf(gran)).year() - n})`,
                    }))} />
                </Space>
              )}
            </Space>
          </Col>
        </Row>

        {this.state.error && (
          <Alert type="error" showIcon style={{ marginBottom: 12 }} message="資料載入發生問題" description={this.state.error}
            action={<Button size="small" danger onClick={this.load}>重試</Button>} closable onClose={() => this.setState({ error: null })} />
        )}

        {/* KPI 卡 (載入中顯示 skeleton；卡片等高避免高度不一致) */}
        <Row gutter={[16, 16]} align="stretch">
          {(breakdownLoading || !kpiCur) ? (
            Array.from({ length: 6 }).map((_, i) => (
              <Col xs={12} sm={8} xl={4} key={`sk${i}`} style={{ display: "flex" }}>
                <Card size="small" style={{ width: "100%" }}>
                  <Skeleton active title={{ width: "55%" }} paragraph={{ rows: 2, width: ["80%", "60%"] }} />
                </Card>
              </Col>
            ))
          ) : (
            this.kpiCards().map((k) => {
              const st = this.statusWord(k.d, k.key === "occupancy" ? 1 : 2);
              return (
                <Col xs={12} sm={8} xl={4} key={k.key} style={{ display: "flex" }}>
                  <Card size="small" style={{ width: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                      <Statistic title={<span>{k.title}<InfoTip id={k.key} title={k.title} /></span>} value={k.value} precision={k.precision} prefix={k.prefix} suffix={k.suffix} valueStyle={{ color: k.color }} />
                      <Spark data={this.metricSeries(k.sk)} color={k.color} />
                    </div>
                    {/* 對比 delta + 狀態徽章：單行不換行，避免各卡高度不一致 */}
                    <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "nowrap", gap: 4 }}>
                      {k.d !== null ? (
                        <span style={{ color: k.d >= 0 ? "#3f8600" : "#cf1322", fontSize: FONT.caption, whiteSpace: "nowrap" }}>
                          {k.d >= 0 ? <RiseOutlined /> : <FallOutlined />} {Math.abs(k.d).toFixed(1)}%
                        </span>
                      ) : <span style={{ fontSize: FONT.caption, color: MUTED_COLOR, whiteSpace: "nowrap" }}>無對比</span>}
                      <Tag color={st.color === "#3f8600" ? "green" : st.color === "#cf1322" ? "red" : "blue"} style={{ margin: 0, whiteSpace: "nowrap", flexShrink: 0 }}>{st.word}</Tag>
                    </div>
                    {k.pv != null && (
                      <div style={{ fontSize: FONT.caption, color: MUTED_COLOR, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {this.prevLabel()} {this.fmtKpi(k.kind, k.pv)}{kpiCmpFull ? `（${kpiCmpFull.from}–${String(kpiCmpFull.to).slice(5)}）` : ""}
                      </div>
                    )}
                    {kpiCur && kpiCur.otb && kpiCur.otb.nights > 0 && this.kpiSubMetric[k.key] && (
                      <div style={{ fontSize: FONT.caption, color: MUTED_COLOR, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        已實現 {this.kpiSub(k.key, kpiCur.realized)} · <span style={{ color: "#d46b08" }}>在手 {this.kpiSub(k.key, kpiCur.otb)}</span>
                      </div>
                    )}
                  </Card>
                </Col>
              );
            })
          )}
        </Row>

        {/* 目標達成：以「期間營收」為目標 = 去年同期「全期最終」營收 ×(1+g)，可調成長率 (存 localStorage)；RevPAR 併於同列標示。
            未來期 (offset>0) 亦顯示：cf (kpiCmpFull) 恆為去年已結束的全期值，基準不受本期未來影響 → 可放行未來期。
            雙進度：主進度為「全期(含在手)」達標率 (attainRev)；下方小字為「已實現進度」(attainRevReal) + pace 註記，
            提醒全期數字含尚未入住的在手訂單，未填滿時會隨時間縮小。 */}
        {kpiCur && cf && tgtRev != null && (
          <Card size="small" style={{ marginTop: 16 }} bodyStyle={{ padding: "12px 16px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: "12px 28px", justifyContent: "space-between" }}>
              {/* 達標 hero：全期(含在手) 達成率 + 已實現進度 */}
              <div style={{ display: "flex", flexDirection: "column", minWidth: 168 }}>
                <span style={{ fontSize: FONT.subtitle, fontWeight: 600 }}>目標達成 <span style={{ fontSize: FONT.caption, color: MUTED_COLOR, fontWeight: 400 }}>· 期間營收</span></span>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 2 }}>
                  <span style={{ fontSize: FONT.display, fontWeight: 700, color: attainRevColor, lineHeight: 1 }}>{attainRev.toFixed(0)}%</span>
                  <span style={{ color: MUTED_COLOR }}>達標</span>
                </div>
                <Progress percent={Math.max(0, Math.min(100, attainRev))} showInfo={false} strokeColor={attainRevColor} size="small" style={{ width: 168, marginTop: 4 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <Progress percent={Math.max(0, Math.min(100, attainRevReal || 0))} showInfo={false} strokeColor={FAINT_COLOR} size="small" style={{ width: 104 }} />
                  <span style={{ fontSize: FONT.caption, color: MUTED_COLOR, whiteSpace: "nowrap" }}>已實現 {attainRevReal != null ? attainRevReal.toFixed(0) : "—"}%</span>
                </div>
              </div>
              {/* 明細統計格 */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 24px", flex: "1 1 320px" }}>
                {this.tgtStat(`目標（${this.prevLabel()}全期 +${this.state.growth}%）`, `NT$${tgtRev.toLocaleString()}`)}
                {this.tgtStat("本期營收", `NT$${Math.round(kpiCur.revenue).toLocaleString()}`, attainRevColor)}
                {this.tgtStat(`${this.prevLabel()}`, `NT$${Math.round(cf.revenue).toLocaleString()}`, undefined, `${cf.from}–${String(cf.to).slice(5)}`)}
                {revYoY != null && this.tgtStat(`較${this.prevLabel()}`, `${revYoY >= 0 ? "+" : "−"}NT$${Math.abs(revYoY).toLocaleString()}`, revYoY >= 0 ? "#3f8600" : "#cf1322", revYoYPct != null ? `${revYoY >= 0 ? "+" : ""}${revYoYPct}%` : null)}
                {tgtRevpar != null && this.tgtStat("RevPAR (參考)", `NT$${Math.round(kpiCur.revpar).toLocaleString()}`, undefined, `目標 NT$${tgtRevpar.toLocaleString()}・達標 ${attain != null ? attain.toFixed(0) : "—"}%`)}
              </div>
              {/* 成長率調整 */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span style={{ color: MUTED_COLOR, marginBottom: 3, whiteSpace: "nowrap" }}>目標成長率 vs {this.prevLabel()}</span>
                <Space size={4}>
                  <Button size="small" onClick={() => this.bumpGrowth(-1)} aria-label="目標成長率減 1 個百分點">−</Button>
                  <InputNumber size="small" aria-label="目標成長率 (%)" value={this.state.growth} min={-50} max={100} step={1}
                    formatter={(v) => `+${v}%`} parser={(v) => (v || "").replace(/[+%\s]/g, "")}
                    onChange={(v) => this.setGrowth(v)} style={{ width: 74 }} />
                  <Button size="small" onClick={() => this.bumpGrowth(1)} aria-label="目標成長率加 1 個百分點">+</Button>
                </Space>
              </div>
            </div>
            <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginTop: 8, borderTop: "1px solid #f5f5f5", paddingTop: 6 }}>
              「達標」以全期(含在手)計，未填滿將隨進單縮小；RevPAR 為避免以降價買住房的守價參考。
            </div>
          </Card>
        )}

        <div style={{ marginTop: 16 }}>
          <PricingActionsCard onPickDate={this.openCurveForDate} />
        </div>

        {/* 趨勢 + 通路 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Card size="small" title={<span>期間走勢 · 住房率 (OCC) vs 平均房價 (ADR)<InfoTip id="occupancy" title="住房率決策" /></span>}
              extra={<Space size="large">
                <span>均值 <b>{cur.occ.toFixed(1)}%</b></span>
                {max && <span style={{ color: "#3f8600" }}><RiseOutlined /> {max.label} {max.occupancy_rate.toFixed(1)}%</span>}
                {min && <span style={{ color: "#cf1322" }}><FallOutlined /> {min.label} {min.occupancy_rate.toFixed(1)}%</span>}
              </Space>}>
              <ResponsiveECharts option={tb.length ? this.trendOption() : null} style={{ height: 300 }}
                ariaLabel={`期間走勢：住房率與平均房價的逐${{ day: "日", isoWeek: "週", month: "月" }[this.unitFor(gran)]}變化，`
                  + `共 ${tb.length} 個資料點，並疊上對比期。詳細數值見下方「本期 vs 對比期明細」表。`} />
              {tb.length ? (
                <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginTop: 2 }}>
                  {`依${{ day: "日", isoWeek: "週", month: "月" }[this.unitFor(gran)]}彙總`}
                  {`　·　${cmpCol}同期比${{ day: "同一個日曆日期", isoWeek: "一年內的第幾週 (ISO)", month: "同一個月份" }[this.unitFor(gran)]}`}
                </div>
              ) : null}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title={<span>訂房來源<InfoTip id="channel" title="通路決策" /></span>}
              extra={<Space size={6}>{this.metricSwitch()}<span style={{ fontSize: FONT.caption }}>合計 <b>{this.mFmt(source.total)}</b></span></Space>} style={{ height: "100%" }}>
              <Spin spinning={breakdownLoading}>
                {source.rows.length ? <ResponsiveECharts option={this.pieOption(source.rows)} style={{ height: 210 }}
                  ariaLabel={`訂房來源佔比圓餅圖：${source.rows.map((r) => `${(r.nam || "").trim()} ${this.mFmt(this.mVal(r))}`).join("、")}。`} /> :
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="無資料" style={{ padding: 40 }} />}
                {source.rows.length ? (
                  <div style={{ marginTop: 10 }}>
                    {source.rows.map((row) => this.splitBarRow(row.name, row, srcMax, "#1890ff", <span>{this.mFmt(row.value)}</span>))}
                  </div>
                ) : null}
                {this.decisionBox(this.sourceDecision())}
              </Spin>
            </Card>
          </Col>
        </Row>

        {/* OTB 前瞻 + 通路類型 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={16}>
            <Card size="small" title={
              <Space size="middle" wrap>
                <span>前瞻 · 未來 {fc.buckets.length} {fc.unit}訂房展望<InfoTip id="forecast" title="訂房展望 (OTB) 決策" /></span>
                {behind.count ? (
                  <Tag color="red" style={{ marginInlineEnd: 0 }}>
                    落後正常帶 {behind.count} 天{behind.actionable ? ` (${LOWER_MAX_LEAD} 天內 ${behind.actionable} 天)` : ""}
                  </Tag>
                ) : null}
              </Space>}
              extra={<Space size="middle" wrap>
                {this.state.forecastView === "table" ? (
                <span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>點日期 → 看該日訂房曲線</span>
              ) : this.state.paceBandLoading ? (
                <span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}><Spin size="small" /><span style={{ marginLeft: 8 }}>計算正常帶 (超前/落後) 中…</span></span>
              ) : this.state.paceBand.length ? (
                <Space size="middle" style={{ fontSize: FONT.caption, color: "#595959" }}>
                  {/* 有團散分層時柱色講的是「團體/散客」，不是訂房進度 —— 這排圖例必須跟著換，
                      否則等於教一套柱子上不存在的顏色語彙。進度改由柱頂的 ▼ 與表格檢視的文字表達 */}
                  {this.hasPaceSplit() ? (
                    // 不再列「超前/正常」：柱色已改講團體/散客，這兩個狀態在圖上沒有對應的視覺。
                    // 切換就在右邊，圖下方註腳與 aria 描述也都指向表格檢視，這裡再寫一次只是把標題擠掉一行
                    <span><span style={{ color: PACE_COLOR.落後, marginRight: 4 }}>▼</span>落後正常帶</span>
                  ) : (
                    <>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: PACE_COLOR.超前, borderRadius: 2, marginRight: 4 }} />超前</span>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: PACE_COLOR.正常, borderRadius: 2, marginRight: 4 }} />正常</span>
                      <span><span style={{ display: "inline-block", width: 10, height: 10, background: PACE_COLOR.落後, borderRadius: 2, marginRight: 4 }} />落後</span>
                      {/* 年前瞻的遠期月份 (近年此時也還沒開賣) 沒有可比基準，另以灰柱表示，不算進超前/正常/落後 */}
                      {this.state.paceBand.some((p) => p.hi < 1) ? (
                        <span><span style={{ display: "inline-block", width: 10, height: 10, background: PACE_COLOR.尚早, borderRadius: 2, marginRight: 4 }} />尚早</span>
                      ) : null}
                    </>
                  )}
                  <span style={{ color: MUTED_COLOR }}>vs 近年同進度</span>
                </Space>
              ) : fc.unit === "天" ? (
                <Space size="middle" style={{ fontSize: FONT.caption, color: "#595959" }}>
                  <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#13c2c2", borderRadius: 2, marginRight: 4 }} />平日</span>
                  <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#faad14", borderRadius: 2, marginRight: 4 }} />週末</span>
                </Space>
                ) : <span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>依 {fc.unit} 彙總</span>}
                {/* 表格不只是「另一種看法」：canvas 對螢幕閱讀器是空白、鑽取又只綁滑鼠，
                    這個切換是圖表唯一的鍵盤與輔助科技等價入口，也是要定位到某一天時唯一可靠的路徑。
                    放在卡片最右側 —— 它是整張卡的模式開關，與標題無關 */}
                <Radio.Group size="small" value={this.state.forecastView} aria-label="前瞻檢視方式"
                  onChange={(e) => this.setPersisted("forecastView", "ops.forecastView", e.target.value)}>
                  <Radio.Button value="chart">圖表</Radio.Button>
                  <Radio.Button value="table">表格</Radio.Button>
                </Radio.Group>
              </Space>}>
              {this.state.forecastView === "table" ? (
                <ForecastTable rows={this.forecastRows()} onPickDate={this.openCurveForBucketDate} />
              ) : (
                <ResponsiveECharts option={fc.buckets.length ? this.otbOption() : null} style={{ height: 200 }} onCategoryClick={this.onForecastBarClick}
                  ariaLabel={`未來 ${fc.buckets.length} 個${fc.unit}的在手展望長條圖。平均在手率 ${fcAvg.toFixed(1)}%，`
                    + `尖峰 ${fcPeak.label} ${fcPeak.occ.toFixed(1)}%。`
                    + (behind.worst ? `落後正常帶 ${behind.count} 天，其中最嚴重的是 ${behind.worst.label}，在手 ${behind.worst.occ}%、正常帶下界 ${behind.worst.bandLo}%。` : "目前沒有落後正常帶的日子。")
                    + (this.hasPaceSplit() ? "每根柱分成團體與散客兩層，落後正常帶的日子在柱頂標 ▼。" : "")
                    + `逐日完整數值請切換到表格檢視。`} />
              )}
              {fc.buckets.length && this.state.forecastView === "chart" ? <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginTop: 2 }}>點任一柱 → 看該{fc.unit === "天" ? "日" : fc.unit === "週" ? "週" : "月"}的完整訂房曲線 (Pickup){this.state.paceBandLoading ? (this.hasPaceSplit() ? "　·　正常帶計算中,完成後落後的日子會在柱頂標 ▼" : "　·　正常帶計算中,完成後柱會依超前/落後重新上色") : this.state.paceBand.length ? "　·　正常帶為近年同進度的近似還原 (歷史 OTB 依現有訂單回推)" : ""}</div> : null}
              {fc.buckets.length ? (
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col xs={12} sm={6}><Statistic title={`平均在手率 (OTB)`} value={fcAvg} precision={1} suffix="%" valueStyle={{ fontSize: FONT.title, whiteSpace: "nowrap" }} /></Col>
                  <Col xs={12} sm={6}><Statistic title={`平均 ADR (OTB)`} value={fcAvgAdr} prefix="NT$" valueStyle={{ fontSize: FONT.title, color: "#fa8c16", whiteSpace: "nowrap" }} /></Col>
                  <Col xs={12} sm={6}>
                    <Button type="text" style={{ height: "auto", padding: 0, textAlign: "left", display: "block", width: "100%" }}
                      onClick={() => fcPeak.m && this.openCurveForBucketDate(fcPeak.m.format("YYYY/MM/DD"))}
                      aria-label={`看尖峰 ${fcPeak.label} 的訂房曲線`}>
                      <Statistic title={`尖峰 ${fcPeak.label}`} value={fcPeak.occ} precision={1} suffix="%" valueStyle={{ fontSize: FONT.title, color: PACE_COLOR.超前 }} />
                    </Button>
                  </Col>
                  {/* 「落後最多」而不是「在手最低」：遠期本來就還沒開賣，取原始最小值永遠指向三個月後。
                      做成按鈕 —— 這是整張卡唯一指名某一天的地方，指到了就該能直接點進去看 */}
                  <Col xs={12} sm={6}>
                    {behind.worst ? (
                      <Button type="text" style={{ height: "auto", padding: 0, textAlign: "left", display: "block", width: "100%" }}
                        onClick={() => this.openCurveForBucketDate(behind.worst.dat)}
                        aria-label={`看落後最多的 ${behind.worst.label} 的訂房曲線`}>
                        <Statistic title={`落後最多 ${behind.worst.label}`} value={behind.worst.occ} precision={1} suffix="%"
                          valueStyle={{ fontSize: FONT.title, color: PACE_COLOR.落後 }} />
                      </Button>
                    ) : (
                      // 正常帶是非阻塞查詢且要數秒，沒回來以前判斷不了落後；直接顯示「—」會讓人
                      // 以為「沒有落後的日子」，那是兩回事
                      <Statistic title="落後最多" value={this.state.paceBandLoading ? "計算中" : "—"}
                        valueStyle={{ fontSize: FONT.title, color: MUTED_COLOR }}
                        suffix={this.state.paceBandLoading ? null : <span style={{ fontSize: FONT.caption }}>無落後日</span>} />
                    )}
                  </Col>
                </Row>
              ) : null}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card size="small" title={<span>通路類型 · 直訂/OTA/旅行社<InfoTip id="channel" title="通路決策" /></span>}
              extra={<Space size={6}>{this.metricSwitch()}<span style={{ fontSize: FONT.caption }}>合計 <b>{this.mFmt(this.channelTypeAgg().total)}</b></span></Space>} style={{ height: "100%" }}>
              <Spin spinning={breakdownLoading}>
                {ctRows.length ? <ResponsiveECharts option={this.pieOption(ctRows)} style={{ height: 190 }}
                  ariaLabel={`通路類型佔比圓餅圖：${ctRows.map((r) => `${(r.nam || "").trim()} ${this.mFmt(this.mVal(r))}`).join("、")}。`} /> :
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="無資料" style={{ padding: 40 }} />}
                {ctRows.length ? (
                  <div style={{ marginTop: 10 }}>
                    {ctRows.map((row) => this.splitBarRow(row.name, row, ctMax, "#1890ff", <span>{this.mFmt(row.value)}</span>))}
                  </div>
                ) : null}
                {this.decisionBox(this.channelDecision())}
              </Spin>
            </Card>
          </Col>
        </Row>

        {/* 訂房增速 (Pickup Velocity)：獨立整列，兩鏡頭上下並列 */}
        {/* 載入中也要把卡片畫出來 (而非等 velocity 到齊才出現)，否則兩張圖的資料來源不同，
            會先是整張卡片消失、再突然冒出來，看起來像畫面壞掉。各圖用自己的載入旗標。 */}
        {this.state.velocityLoading || this.state.pickupLoading || this.state.velocity.length || this.state.pickup.length ? (
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Card size="small" title={<span>訂房增速 (Pickup Velocity)<InfoTip id="velocity" title="訂房增速 (Pickup Velocity)" /></span>}
                extra={this.state.velocityLoading ? <Spin size="small" /> : velTrend}>
                <div style={{ color: "#595959" }}>依入住日 — 各未來日近 30 日淨進單 (哪些未來日在加溫/降溫)
                  {this.state.pickupLoading
                    ? <span style={{ color: FAINT_COLOR }}>　·　<Spin size="small" /> 載入中…</span>
                    : <span style={{ color: FAINT_COLOR }}>·點柱看訂單</span>}
                </div>
                {this.velocityChart("stay", this.state.pickupLoading, this.state.pickup.length, this.onVelocityStayClick)}
                <div style={{ color: "#595959", marginTop: 8, borderTop: "1px dashed #f0f0f0", paddingTop: 6 }}>依訂房日 — 近 {this.velDays()} 訂房日 · 每{({ day: "日", isoWeek: "週", month: "月" }[this.unitFor(this.state.gran)] || "日")}新增 (最近接單快不快)
                  {this.state.velocityLoading
                    ? <span style={{ color: FAINT_COLOR }}>　·　<Spin size="small" /> 載入中…</span>
                    : <span style={{ color: FAINT_COLOR }}>·點柱看訂單</span>}
                </div>
                {this.velocityChart("book", this.state.velocityLoading, this.state.velocity.length, this.onVelocityBookClick)}
              </Card>
            </Col>
          </Row>
        ) : null}

        {/* 房型維度 (欄位多，獨佔整列以免關鍵決策欄被水平捲動遮住) */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card size="small" title={<span>房型維度 · 各房型表現與決策<InfoTip id="roomtype" title="房型決策" /></span>}
              extra={<span style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>上期 = {this.prevLabel()}</span>} style={{ height: "100%" }}>
              <Spin spinning={breakdownLoading}>
                <Table
                  rowKey="room_cod" size="small" pagination={false} scroll={{ x: "max-content" }}
                  dataSource={roomType}
                  locale={{ emptyText: "無資料" }}
                  columns={withKeyboardSort([
                    { title: "房型", key: "room", width: 150, fixed: this.state.narrow ? undefined : "left", sorter: (a, b) => String(a.room_nam || a.room_cod).localeCompare(String(b.room_nam || b.room_cod), "zh-Hant"), render: (_, r) => <span>{r.room_nam || r.room_cod} <span style={{ color: FAINT_COLOR, fontSize: FONT.caption }}>{r.room_cod}</span></span> },
                    { title: "房數", dataIndex: "phys_rooms", key: "phys_rooms", align: "right", sorter: (a, b) => (a.phys_rooms || 0) - (b.phys_rooms || 0), sortDirections: ["descend", "ascend"] },
                    { title: "間夜", dataIndex: "nights", key: "nights", align: "right", sorter: (a, b) => (a.nights || 0) - (b.nights || 0), sortDirections: ["descend", "ascend"], render: (v, r) => {
                        const p = rtCmpMap[String(r.room_cod).trim()];
                        const oN = Number(r.o_nights) || 0;
                        return <div>
                          <div>{Number(v).toLocaleString()}{this.rtCmp(v, p ? p.nights : null, (x) => Number(x).toLocaleString())}</div>
                          <div style={{ width: 80, marginLeft: "auto", marginTop: 3 }}>{this.splitBar(Number(r.r_nights) || 0, oN, rtMaxNights, "#1890ff")}</div>
                          {oN > 0 && <div style={{ fontSize: FONT.caption, color: "#d46b08" }}>(在手 {oN.toLocaleString()})</div>}
                        </div>;
                      } },
                    { title: "佔比", key: "share", align: "right", sorter: (a, b) => (a.nights || 0) - (b.nights || 0), sortDirections: ["descend", "ascend"], render: (_, r) => (r.nights / rtTotalNights * 100).toFixed(0) + "%" },
                    { title: "住房率", dataIndex: "occ", key: "occ", align: "right", sorter: (a, b) => (a.occ == null ? -1 : a.occ) - (b.occ == null ? -1 : b.occ), sortDirections: ["descend", "ascend"], render: (v, r) => {
                        const p = rtCmpMap[String(r.room_cod).trim()];
                        const col = v == null ? undefined : v < 40 ? "#cf1322" : v > 75 ? "#3f8600" : undefined;
                        return <div>
                          <span style={{ color: col }}>{v == null ? "—" : Number(v).toFixed(0) + "%"}</span>
                          {r.occ_basis === "onbook" && <Tag color="blue" style={{ marginLeft: 4, transform: "scale(0.85)" }}>在手</Tag>}
                          {this.rtCmp(v, p ? p.occ : null, (x) => Number(x).toFixed(0) + "%")}
                        </div>;
                      } },
                    // 滿房天數：住房率是整段平均，看不出「有幾天根本沒房可賣」。
                    // 77% 的房型可能是天天七成，也可能是半個月滿房、半個月空著 —— 後者要漲價，前者要衝量
                    { title: <Tooltip title="期間內「一間不剩」的天數 (RMINV_MN 逐日庫存，含未來)。天數多代表需求撞到房數上限，該用價格而非促銷去化"><span style={{ borderBottom: `1px dashed ${MARK_MUTED_COLOR}`, cursor: "help" }}>滿房天數</span></Tooltip>, key: "full_days", width: 110, align: "right",
                      sorter: (a, b) => rtFullRatio(a) - rtFullRatio(b), sortDirections: ["descend", "ascend"],
                      render: (_, r) => {
                        if (r.full_days == null || !r.inv_days) return <span style={{ color: FAINT_COLOR }}>—</span>;
                        const ratio = r.full_days / r.inv_days;
                        // 三分之一以上的日子售罄就值得標紅 —— 那已經不是零星尖峰，是結構性缺房
                        const col = ratio >= 0.34 ? "#cf1322" : ratio >= 0.15 ? "#d46b08" : undefined;
                        // 鎖控未釋出的天數要分開講：自由庫存賣完不等於沒房，那些天釋出鎖控就還能賣
                        const blocked = (r.free0_days || 0) - r.full_days;
                        const cell = (
                          <span style={{ color: col, fontWeight: col ? 600 : undefined }}>
                            {r.full_days} <span style={{ color: FAINT_COLOR, fontWeight: 400 }}>/ {r.inv_days} 天</span>
                          </span>
                        );
                        return blocked > 0
                          ? <Tooltip title={`另有 ${blocked} 天自由庫存已售罄、但仍有鎖控未釋出 —— 那幾天釋出就還能賣`}>
                              <span style={{ borderBottom: `1px dotted ${MARK_MUTED_COLOR}` }}>{cell}</span>
                            </Tooltip>
                          : cell;
                      } },
                    { title: "平均房價 (ADR)", dataIndex: "adr", key: "adr", align: "right", sorter: (a, b) => (a.adr || 0) - (b.adr || 0), sortDirections: ["descend", "ascend"], render: (v, r) => {
                        const p = rtCmpMap[String(r.room_cod).trim()];
                        return <div>{Number(v).toLocaleString()}{this.rtCmp(v, p ? p.adr : null, (x) => Number(x).toLocaleString())}</div>;
                      } },
                    { title: "RevPAR", dataIndex: "revpar", key: "revpar", align: "right", sorter: (a, b) => (a.revpar || 0) - (b.revpar || 0), sortDirections: ["descend", "ascend"], defaultSortOrder: "descend", render: (v, r) => {
                        const p = rtCmpMap[String(r.room_cod).trim()];
                        return <div>{v == null ? "—" : Number(v).toLocaleString()}{this.rtCmp(v, p ? p.revpar : null, (x) => Number(x).toLocaleString())}</div>;
                      } },
                    { title: "營收", dataIndex: "rev", key: "rev", align: "right", sorter: (a, b) => (a.rev || 0) - (b.rev || 0), sortDirections: ["descend", "ascend"], render: (v, r) => {
                        const p = rtCmpMap[String(r.room_cod).trim()];
                        return <div>{Number(v).toLocaleString()}{this.rtCmp(v, p ? p.rev : null, (x) => Number(x).toLocaleString())}</div>;
                      } },
                    { title: <Tooltip title="待售間夜 × ADR：此房型若補滿、以目前單價估的潛在房租空間 (促銷通常需略降價，屬上限參考)"><span style={{ borderBottom: `1px dashed ${MARK_MUTED_COLOR}`, cursor: "help" }}>營收空間</span></Tooltip>, key: "upside", align: "right", sorter: (a, b) => rtUpside(a) - rtUpside(b), sortDirections: ["descend", "ascend"], render: (_, r) => {
                        const up = rtUpside(r), un = rtUnsold(r);
                        return <div>
                          <span style={{ fontWeight: 600, color: up > 0 ? "#fa8c16" : undefined }}>{up > 0 ? "NT$" + Math.round(up).toLocaleString() : "—"}</span>
                          <div style={{ fontSize: FONT.caption, color: MUTED_COLOR }}>待售 {un.toLocaleString()} 夜</div>
                        </div>;
                      } },
                    { title: <Tooltip title="近 30 天成立−取消、落在本期的淨房晚：正=仍在進單 (售罄動能，可評估放量/升等)、負=淨退訂或停滯。過去期不適用。"><span style={{ borderBottom: `1px dashed ${MARK_MUTED_COLOR}`, cursor: "help" }}>近期進單</span></Tooltip>, key: "pickup", align: "right", sorter: (a, b) => ((rtPick(a) || {}).net || 0) - ((rtPick(b) || {}).net || 0), sortDirections: ["descend", "ascend"], render: (_, r) => {
                        const p = rtPick(r);
                        if (!p) return <span style={{ color: FAINT_COLOR }}>—</span>;
                        const n = p.net;
                        const cod = String(r.room_cod).trim();
                        // 點進去看這個房型本期的訂房曲線：淨進單只講「最近 30 天」這一個切片，
                        // 曲線才看得出整條進帳節奏是超前還是落後。整格可點就必須鍵盤也能觸發
                        const open = () => this.openCurveForRoom(cod);
                        return (
                          <Tooltip title={`近 30 日 新增 ${p.added} 夜 · 取消 ${p.cancelled} 夜 —— 點選看此房型本期的訂房曲線`}>
                            <span role="button" tabIndex={0} onClick={open}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
                              /* 用內距把命中區撐大、再用等量負邊距抵銷 —— 版面完全不動，
                                 但可點面積從 42×17 變成 58×41。手指的接觸面約 40px 寬，
                                 17px 高的目標在手機上按十次會有幾次落空 */
                              style={{ color: n > 0 ? "#3f8600" : n < 0 ? "#cf1322" : MUTED_COLOR,
                                cursor: "pointer", borderBottom: `1px dotted ${MARK_MUTED_COLOR}`,
                                display: "inline-block", padding: "12px 8px", margin: "-12px -8px" }}>
                              {n > 0 ? "+" : ""}{n} 夜
                            </span>
                          </Tooltip>
                        );
                      } },
                    { title: "決策建議", key: "decision", sorter: (a, b) => (a.occ == null ? -1 : a.occ) - (b.occ == null ? -1 : b.occ), sortDirections: ["descend", "ascend"], render: (_, r) => {
                        if (r.occ == null) return "—";
                        // 去化 (住房率 vs 整體 ±8pp) × 產值 (RevPAR vs 整體 ±15%)；近滿 (≥92%) 依 pace 判放量。
                        const bOcc = (kpiCur && kpiCur.occ != null) ? kpiCur.occ : cur.occ, bRev = (kpiCur && kpiCur.revpar) || 0;
                        const occHi = r.occ >= bOcc + 8, occLo = r.occ <= bOcc - 8;
                        const revHi = bRev > 0 && r.revpar != null && r.revpar >= bRev * 1.15;
                        const revLo = bRev > 0 && r.revpar != null && r.revpar <= bRev * 0.85;
                        const pk = rtPick(r), net = pk ? pk.net : null;
                        let color = "default", label = "與整體相當";
                        if (r.occ >= 92) {
                          if (net != null && net > 0) { label = revLo ? "近滿仍進單·單價低該拉價" : "近滿仍進單·放量/升等"; color = revLo ? "blue" : "green"; }
                          else { label = "近滿·守價"; color = "green"; }
                        } else if (occHi && revLo) { label = "去化佳單價低·可拉價"; color = "blue"; }
                        else if (occHi) { label = "去化佳·守價限折"; color = "green"; }
                        else if (occLo && revHi) { label = "高單價撐·精準促銷"; color = "gold"; }
                        else if (occLo) { label = "去化慢·促銷/開OTA"; color = "orange"; }
                        const tip = (
                          <div style={{ lineHeight: 1.7 }}>
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>判斷基準 (相對整體)</div>
                            <div>· 住房率 <b>{Number(r.occ).toFixed(0)}%</b> vs 整體 {Number(bOcc).toFixed(0)}%　高門檻 {Number(bOcc + 8).toFixed(0)}% / 低門檻 {Number(bOcc - 8).toFixed(0)}%{r.occ >= 92 ? "，另 ≥92% 視為近滿" : ""}</div>
                            <div>· RevPAR <b>{r.revpar != null ? "NT$" + Math.round(r.revpar).toLocaleString() : "—"}</b> vs 整體 NT${Math.round(bRev).toLocaleString()}　高門檻 ×1.15={Math.round(bRev * 1.15).toLocaleString()} / 低門檻 ×0.85={Math.round(bRev * 0.85).toLocaleString()}</div>
                            <div>· 近期進單 <b>{net == null ? "—" : (net > 0 ? "+" : "") + net + " 夜"}</b>{r.occ >= 92 ? " (近滿時 >0 才建議放量)" : ""}</div>
                          </div>
                        );
                        return <Tooltip title={tip} overlayStyle={{ maxWidth: 360 }}><span style={{ cursor: "help" }}><Tag color={color}>{label}</Tag></span></Tooltip>;
                      } },
                  ])}
                />
              </Spin>
            </Card>
          </Col>
        </Row>

        {/* 銷售方案 Top */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Card size="small" title="銷售方案 · Top 方案" extra={<Space size={6}>{this.metricSwitch()}<span style={{ fontSize: FONT.caption }}>合計 <b>{this.mFmt(plan.total)}</b></span></Space>} style={{ height: "100%" }}>
              <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginBottom: 8 }}>房晚 = 期間住用房晚 (實際售出)</div>
              <Spin spinning={breakdownLoading}>
                {plan.rows.length ? plan.rows.map((p) => this.splitBarRow(p.name, p, planMax, "#13c2c2", <span>{this.mFmt(p.value)} · {p.share.toFixed(1)}% <span style={{ color: MUTED_COLOR }}>· ADR {Number(p.adr).toLocaleString()}</span></span>)) :
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="無資料" />}
              </Spin>
            </Card>
          </Col>
        </Row>

        {/* 部門營收 + 住客體驗 */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} lg={12}>
            <Card size="small" title={<span>部門營收 · 各部門貢獻<InfoTip id="revenue" title="營收決策" /></span>} style={{ height: "100%" }} bodyStyle={{ minHeight: 220 }}>
              <Spin spinning={breakdownLoading}>
                {deptRows.length ? deptRows.map((d) => {
                  const oN = Number(d.o) || 0;
                  const rN = d.r != null ? Number(d.r) || 0 : Number(d.v) || 0;
                  return (
                    <div key={d.label} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span>{d.label}</span>
                        <span style={{ fontWeight: 600 }}>
                          NT$ {d.v.toLocaleString()}
                          {oN > 0 && <span style={{ fontWeight: 400, color: "#d46b08", fontSize: FONT.caption, marginLeft: 6 }}>(在手 NT$ {oN.toLocaleString()})</span>}
                        </span>
                      </div>
                      {this.splitBar(rN, oN, deptMax, d.color)}
                    </div>
                  );
                }) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="無資料" />}
              </Spin>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card size="small" title="住客體驗 · 滿意度與風險" style={{ height: "100%" }} bodyStyle={{ minHeight: 220 }}>
              <Row gutter={12}>
                <Col span={12}>
                  <Statistic title="取消率 (訂單)" value={cancelRate} precision={1} suffix="%" valueStyle={{ color: cancelRate > 30 ? "#cf1322" : "#595959" }} />
                </Col>
                <Col span={12}>
                  <Statistic title="No-show 率" value={noshowPct == null ? "—" : noshowPct} precision={1} suffix={noshowPct == null ? "" : "%"} valueStyle={{ color: noshowPct != null && noshowPct > 3 ? "#cf1322" : "#595959" }} />
                </Col>
              </Row>
              <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginTop: 2 }}>取消率 = 該入住日已取消訂房 / 全部訂房 (ORDER_DT)</div>
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
                <div style={{ marginBottom: 4 }}>
                  <span style={{ color: MUTED_COLOR, fontSize: FONT.caption }}>住客回饋</span>
                </div>
                <SatisfactionSummary
                  data={this.state.feedback}
                  error={this.state.feedbackError}
                  onViewMore={() => {
                    if (this.feedbackCardRef.current) {
                      this.feedbackCardRef.current.openDrawer();
                    }
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>

        {/* 住客回饋摘要。緊接在住客體驗卡之後，讓指標與展開內容相鄰。
            資料由 fetchFeedback() 取一次共用，卡片本身不再自取。 */}
        <div style={{ marginTop: 16 }}>
          <FeedbackSummaryCard
            ref={this.feedbackCardRef}
            data={this.state.feedback}
            error={this.state.feedbackError}
            needs={this.state.feedbackNeeds}
            needsError={this.state.feedbackNeedsError}
            needsPrevLabel={this.prevLabel()}
            from={this.range(this.state.gran).from.format("YYYY/MM/DD")}
            to={this.range(this.state.gran).to.format("YYYY/MM/DD")}
          />
        </div>

        {/* 明細對照：本期各桶 vs 去年同期同一桶 (依日期對齊，非位置) */}
        <Card size="small" style={{ marginTop: 16 }}
          title={`明細對照 · ${this.periodTitle()} vs ${this.prevLabel()} ${this.prevRangeLabel(gran)}`}>
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            scroll={{ x: 1360 }}
            dataSource={cmpRows}
            columns={withKeyboardSort([
              { title: "期間", children: [
                { title: "本期", dataIndex: "cDat", key: "cd", width: 170, sorter: (x, y) => String(x.cDat || "").localeCompare(String(y.cDat || "")), defaultSortOrder: "ascend", render: (d, row) => d ? (
                  <span style={{ color: row.phase === "otb" ? FAINT_COLOR : undefined }}>
                    {isDayRow ? <DateWithWeekday date={d} /> : d}
                    {/* 在手不另標籤：該列已整列灰字表達，日期欄還要留給假日標示 */}
                    {row.phase === "mixed" && <Tag color="gold" style={{ marginLeft: 6 }}>部分</Tag>}
                  </span>
                ) : "—" },
                { title: "對比", dataIndex: "pDat", key: "pd", width: 170, render: (d) => d ? <span style={{ color: MUTED_COLOR }}>{isDayRow ? <DateWithWeekday date={d} /> : d}</span> : "—" },
              ] },
              { title: "住房率", children: [
                { title: "本期", dataIndex: "cOcc", key: "co", width: 100, align: "right", render: (v) => (v == null ? "—" : v.toFixed(1) + "%") },
                { title: cmpCol, dataIndex: "pOcc", key: "po", width: 100, align: "right", render: (v) => (v == null ? "—" : <span style={{ color: MUTED_COLOR }}>{v.toFixed(1) + "%"}</span>) },
                { title: "差", key: "do", sorter: (x, y) => ((x.cOcc ?? 0) - (x.pOcc ?? 0)) - ((y.cOcc ?? 0) - (y.pOcc ?? 0)), sortDirections: ["ascend", "descend"], width: 100, align: "right", render: (_, r) => (r.cOcc != null && r.pOcc != null) ? <span style={{ color: r.cOcc >= r.pOcc ? "#3f8600" : "#cf1322" }}>{(r.cOcc - r.pOcc >= 0 ? "+" : "") + (r.cOcc - r.pOcc).toFixed(1)}pt</span> : "—" },
              ] },
              { title: "ADR", children: [
                { title: "本期", dataIndex: "cAdr", key: "ca", width: 110, align: "right", render: (v) => (v == null ? "—" : Math.round(v).toLocaleString()) },
                { title: cmpCol, dataIndex: "pAdr", key: "pa", width: 110, align: "right", render: (v) => (v == null ? "—" : <span style={{ color: MUTED_COLOR }}>{Math.round(v).toLocaleString()}</span>) },
                { title: "差", key: "da", sorter: (x, y) => ((x.cAdr ?? 0) - (x.pAdr ?? 0)) - ((y.cAdr ?? 0) - (y.pAdr ?? 0)), sortDirections: ["ascend", "descend"], width: 110, align: "right", render: (_, r) => (r.cAdr != null && r.pAdr != null) ? <span style={{ color: r.cAdr >= r.pAdr ? "#3f8600" : "#cf1322" }}>{(r.cAdr - r.pAdr >= 0 ? "+" : "") + Math.round(r.cAdr - r.pAdr).toLocaleString()}</span> : "—" },
              ] },
              { title: "營收", children: [
                { title: "本期", dataIndex: "cRev", key: "cr", width: 130, align: "right", render: (v) => (v == null ? "—" : Math.round(v).toLocaleString()) },
                { title: cmpCol, dataIndex: "pRev", key: "pr", width: 130, align: "right", render: (v) => (v == null ? "—" : <span style={{ color: MUTED_COLOR }}>{Math.round(v).toLocaleString()}</span>) },
                { title: "差", key: "dr", sorter: (x, y) => ((x.cRev ?? 0) - (x.pRev ?? 0)) - ((y.cRev ?? 0) - (y.pRev ?? 0)), sortDirections: ["ascend", "descend"], width: 130, align: "right", render: (_, r) => (r.cRev != null && r.pRev != null) ? <span style={{ color: r.cRev >= r.pRev ? "#3f8600" : "#cf1322" }}>{(r.cRev - r.pRev >= 0 ? "+" : "") + Math.round(r.cRev - r.pRev).toLocaleString()}</span> : "—" },
              ] },
            ])}
            summary={() => {
              const t = cmpTotal;
              const num = (v) => (v == null ? "—" : Math.round(v).toLocaleString());
              const diff = (c, p, fmt) => (c == null || p == null) ? "—" : (
                <b style={{ color: c >= p ? "#3f8600" : "#cf1322" }}>{(c - p >= 0 ? "+" : "") + fmt(c - p)}</b>
              );
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: "#fafafa" }}>
                    <Table.Summary.Cell index={0}><b>小計</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <span style={{ color: MUTED_COLOR }}>
                        {t.matched === t.buckets ? `${t.buckets} ${detailUnitWord}` : `${t.matched}/${t.buckets} ${detailUnitWord}有對比`}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right"><b>{t.cOcc == null ? "—" : t.cOcc.toFixed(1) + "%"}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><span style={{ color: MUTED_COLOR }}>{t.pOcc == null ? "—" : t.pOcc.toFixed(1) + "%"}</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">{diff(t.cOcc, t.pOcc, (d) => d.toFixed(1) + "pt")}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right"><b>{num(t.cAdr)}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right"><span style={{ color: MUTED_COLOR }}>{num(t.pAdr)}</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">{diff(t.cAdr, t.pAdr, (d) => Math.round(d).toLocaleString())}</Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right"><b>{num(t.cRev)}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right"><span style={{ color: MUTED_COLOR }}>{num(t.pRev)}</span></Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">{diff(t.cRev, t.pRev, (d) => Math.round(d).toLocaleString())}</Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </Card>

        {/* 訂單明細抽屜：三個入口共用 —— 兩張增速圖的柱 (進出) 與訂房曲線抽屜的「目前在手訂單」 */}
        {/* zIndex 高於訂房曲線抽屜 (預設 1000)：這張表也會從曲線抽屜裡開，疊在它上面才看得到 */}
        <OrdersDrawer open={this.state.ordersOpen} title={this.state.ordersTitle} zIndex={1050}
          loading={this.state.ordersLoading} data={this.state.ordersData} error={this.state.ordersError}
          onClose={() => this.setState({ ordersOpen: false })} />

        {/* 訂房曲線 (Pickup) 抽屜：點前瞻柱、定價清單日期、或房型維度的近期進單開啟。
            區間與房型留在這裡算 (三個入口各自要用儀錶板的區間語彙)，抽屜只負責畫 */}
        <CurveDrawer open={this.state.curveOpen} stay={this.state.curveStay}
          curveData={this.state.curveData} curLead={this.state.curveCurLead} loading={this.state.curveLoading}
          room={this.state.curveRoom} roomTypes={(this.state.breakdown && this.state.breakdown.roomType) || []}
          onRoomChange={this.setCurveRoom} onOpenOrders={this.openOtbOrders}
          onStep={this.stepCurveDay} canStep={!!this.state.curveFrom && this.state.curveFrom === this.state.curveTo}
          onClose={() => this.setState({ curveOpen: false })} />
      </Spin>
    );
  }
}

export default OpsDashboard;
